import { useState, useMemo, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Combobox } from "@/components/ui/combobox";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Stepper, Step } from "@/components/ui/stepper";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Package, Store as StoreIcon, ArrowLeft, ArrowRight, Calendar, Building2, Eye, Settings, MousePointer, ShoppingCart, AlertCircle, Info, Upload } from "lucide-react";
import { LoadingSpinner, PageLoadingSpinner, InlineLoadingSpinner } from "@/components/loading-spinner";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { createCampaign } from "@/lib/api";
import { apiFetch, getJson, getJsonUnwrapped, unwrapPaginatedResponse } from "@/lib/http";
import type {
  Retailer, Store, Pack, MediaFormat, BookingCycle,
  InsertCampaign, Task
} from "@shared/schema";
import { dbg } from "@/lib/dbg";
import { useLocation } from "wouter";
import { YearFilter } from "@/components/filters/YearFilter";
import { useYear } from "@/state/yearStore";
import { resolveManualCampaignBooking, getAvailableBands } from "@/lib/resolveManualCampaignBooking";
import { getCampaignIntegrityUserMessage } from "@/lib/campaignIntegrityErrors";
import { useAuthUser } from "@/hooks/useAuthUser";
import { StoreFormatGroupEditor } from "@/components/campaigns/StoreFormatGroupEditor";
import { ExecutionPreview } from "@/components/campaigns/ExecutionPreview";
import type { StoreFormatGroup } from "@shared/store-format-groups";
import { isPopadGroup, flattenGroups } from "@shared/store-format-groups";

const normalizeCycle = (cycle: any) => {
  if (!cycle || typeof cycle !== "object") {
    return cycle;
  }

  const startDate = cycle.startDate ?? cycle.start_date ?? null;
  const endDate = cycle.endDate ?? cycle.end_date ?? null;
  const bookingDeadline = cycle.bookingDeadline ?? cycle.booking_deadline ?? null;
  const retailerId = cycle.retailerId ?? cycle.retailer_id ?? null;

  return {
    ...cycle,
    startDate,
    start_date: startDate,
    endDate,
    end_date: endDate,
    bookingDeadline,
    booking_deadline: bookingDeadline,
    retailerId,
    retailer_id: retailerId,
  };
};

const campaignSchema = z.object({
  name: z.string().min(1, "Campaign name is required"),
  description: z.string().optional(),
  statusId: z.number().optional(),
  cycleId: z.number().optional(),
  campaignTypeKey: z.string().optional(),
  createRemoval: z.boolean().optional(),
  advertiserId: z.number().optional().nullable(),
});

export interface MediaSelection {
  mediaFormatId: number;
  quantity: number;
  storeId?: number;  // Optional: for per-store media like POPAds
}

export interface StoreSelection {
  type: 'store' | 'pack';
  id: number;
}

type CampaignFormData = z.infer<typeof campaignSchema>;

type CampaignStatus = {
  id: number;
  name: string;
  color?: string | null;
};

const buildCampaignPayload = ({
  formData,
  retailerId,
  cycleId,
  packs,
  stores,
  media,
  campaignType,
  manualTaskId,
}: {
  formData: CampaignFormData;
  retailerId: number | null;
  cycleId: number | null;
  packs: number[];
  stores: StoreSelection[];
  media: MediaSelection[];
  campaignType: 'manual' | 'manual-groups' | 'pack' | null;
  manualTaskId?: number | null;
}) => {
  const baseCampaign = {
    ...formData,
    retailerId: retailerId ?? undefined,
    cycleId: cycleId ?? undefined,
    statusId: formData.statusId ?? 1,
    createRemoval: formData.createRemoval ?? false,
    campaignTypeKey: formData.campaignTypeKey ?? (campaignType === 'pack' ? 'Pack' : 'Standard'),
    advertiserId: formData.advertiserId ?? undefined,
  };

  // Both 'manual' and 'manual-groups' are treated as manual campaigns
  if (campaignType === 'manual' || campaignType === 'manual-groups') {
    const normalizedManualTaskId = typeof manualTaskId === 'number' && Number.isFinite(manualTaskId)
      ? Number(manualTaskId)
      : undefined;

    if (normalizedManualTaskId) {
      (baseCampaign as Record<string, unknown>).manualTaskId = normalizedManualTaskId;
      (baseCampaign as Record<string, unknown>).manual_task_id = normalizedManualTaskId;
    }
  }

  const storeIds = stores
    .filter(selection => selection.type === 'store')
    .map(selection => selection.id);

  return {
    campaign: baseCampaign,
    retailers: retailerId ? [retailerId] : [],
    stores: storeIds,
    packs: campaignType === 'pack' ? packs : [], // Only include packs for pack-based campaigns
    media,
  };
};

function PackCard({ pack, isSelected, onToggle, isDisabled = false, disableReason = "", lockStatus = 'unlocked', snapshotData }: {
  pack: any;
  isSelected: boolean;
  onToggle: () => void;
  isDisabled?: boolean;
  disableReason?: string;
  lockStatus?: 'locked' | 'unlocked' | 'loading';
  snapshotData?: any;
}) {
  const [packDetails, setPackDetails] = useState<{
    stores: any[];
    templates: any[];
  } | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  // Check if this pack is flexible (has multiple format group combinations)
  const isFlexiblePack = (): boolean => {
    if (!pack) return false;

    // Check if pack has formatGroups with multiple groups
    if (pack.formatGroups) {
      try {
        const groups = typeof pack.formatGroups === 'string'
          ? JSON.parse(pack.formatGroups)
          : pack.formatGroups;
        return Array.isArray(groups) && groups.length > 1;
      } catch (error) {
        console.error("Error parsing formatGroups:", error);
        return false;
      }
    }

    // Check if pack has flexibleFormatGroup flag
    return !!pack.flexibleFormatGroup;
  };

  // Remove the usePackSnapshot hook call - data is now passed as prop

  const loadPackDetails = async () => {
    if (packDetails || loading) return;

    setLoading(true);
    try {
      if (snapshotData?.snapshot) {
        setPackDetails({
          stores: snapshotData.snapshot.stores || [],
          templates: snapshotData.snapshot.templates || []
        });
      } else {
        setPackDetails({ stores: [], templates: [] });
      }
    } catch (error) {
      console.error('Error loading pack details:', error);
      setPackDetails({ stores: [], templates: [] });
    } finally {
      setLoading(false);
    }
  };

  const cardContent = (
    <Card className={cn(
      "transition-colors",
      isSelected ? "ring-2 ring-primary" : "",
      isDisabled || lockStatus === 'unlocked' ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-muted"
    )}>
      <CardContent className="p-4">
        <div className="flex items-start space-x-3">
          <Checkbox
            checked={isSelected}
            onCheckedChange={isDisabled || lockStatus === 'unlocked' ? undefined : onToggle}
            disabled={isDisabled || lockStatus === 'unlocked'}
            className="mt-1"
          />
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className={cn("font-medium", isDisabled && "text-muted-foreground")}>
                  {pack.name}
                </h3>
                {isFlexiblePack() && (
                  <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                    Flexible
                  </Badge>
                )}
                {lockStatus === 'locked' && (
                  <Badge variant="success" className="text-xs">
                    Locked
                  </Badge>
                )}
                {lockStatus === 'unlocked' && (
                  <Badge variant="secondary" className="text-xs bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                    Unlocked
                  </Badge>
                )}
                {lockStatus === 'loading' && (
                  <Badge variant="outline" className="text-xs">
                    Loading...
                  </Badge>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
                  if (!isExpanded) loadPackDetails();
                }}
                disabled={isDisabled}
              >
                <Eye className="h-4 w-4" />
              </Button>
            </div>
            <p className={cn(
              "text-sm mb-2",
              isDisabled ? "text-muted-foreground/60" : "text-muted-foreground"
            )}>
              {pack.description}
            </p>
            {isDisabled && disableReason && (
              <p className="text-xs text-red-600 dark:text-red-400 mb-2">
                {disableReason}
              </p>
            )}

            {isExpanded && (
              <div className="mt-3 space-y-2">
                {loading ? (
                  <div className="text-xs text-muted-foreground">Loading details...</div>
                ) : packDetails ? (
                  <div className="space-y-2">
                    <div>
                      <div className="text-xs font-medium text-muted-foreground">
                        Stores ({packDetails.stores.length})
                      </div>
                      <div className="text-xs">
                        {Array.isArray(packDetails.stores) && packDetails.stores.length > 0
                          ? packDetails.stores.slice(0, 3).map((store: any) => store.store?.name || 'Unknown').join(', ') +
                          (packDetails.stores.length > 3 ? ` +${packDetails.stores.length - 3} more` : '')
                          : 'No stores configured'
                        }
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-muted-foreground">
                        Media Formats ({packDetails.templates.length})
                      </div>
                      <div className="text-xs">
                        {packDetails.templates.slice(0, 2).map((template: any) => `${template.mediaFormat?.name || 'Unknown'} (${template.quantity})`).join(', ')}
                        {packDetails.templates.length > 2 && ` +${packDetails.templates.length - 2} more`}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground">Failed to load details</div>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (lockStatus === 'unlocked') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {cardContent}
          </TooltipTrigger>
          <TooltipContent>
            <p>This pack must be locked in Retailer Management before it can be added to a campaign.</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return cardContent;
}

export default function CampaignCreation() {
  const [currentStep, setCurrentStep] = useState(1);
  const [campaignType, setCampaignType] = useState<'manual' | 'manual-groups' | 'pack' | null>(null);

  // Get current user for role-based UI visibility
  const { user: authUser } = useAuthUser();
  const isAdmin = authUser?.roles?.includes('admin') || authUser?.role === 'admin';
  const [selectedRetailerId, setSelectedRetailerId] = useState<number | null>(null);
  const [selectedCycleId, setSelectedCycleId] = useState<number | null>(null);
  const [selectedStores, setSelectedStores] = useState<StoreSelection[]>([]);
  const [selectedPacks, setSelectedPacks] = useState<number[]>([]);
  const [selectedMedia, setSelectedMedia] = useState<MediaSelection[]>([]);
  const [selectedManualTaskId, setSelectedManualTaskId] = useState<number | null>(null);
  const [formData, setFormData] = useState<Partial<CampaignFormData>>({});
  const [packStores, setPackStores] = useState<any[]>([]);
  const [packTemplates, setPackTemplates] = useState<any[]>([]);
  const [flexiblePackWarning, setFlexiblePackWarning] = useState<string>("");
  const [isWaitingForSnapshot, setIsWaitingForSnapshot] = useState(false);
  const [snapshotStatus, setSnapshotStatus] = useState<string>("");

  // Store + Format Groups state (for manual-groups campaign type)
  const [selectedGroups, setSelectedGroups] = useState<StoreFormatGroup[]>([]);

  const [, setLocation] = useLocation();

  // Global year filter for cycle selection
  const selectedYear = useYear();

  // POPAd (Limited-Capacity Format) state - per-format tracking
  const [enabledPopadFormats, setEnabledPopadFormats] = useState<Set<number>>(new Set());
  const [popadFormatCoverages, setPopadFormatCoverages] = useState<Map<number, number>>(new Map());
  const [popadStoreOverrides, setPopadStoreOverrides] = useState<Map<string, number>>(new Map());

  // Helper to check if any POPAd format is enabled
  const popadEnabled = enabledPopadFormats.size > 0;
  // Helper to get default coverage (for backward compatibility with single-format logic)
  const popadDefaultCoverage = popadFormatCoverages.size > 0 ? Array.from(popadFormatCoverages.values())[0] : null;
  const getPopadStoreOverrideKey = (storeId: number, formatId: number) => `${storeId}-${formatId}`;
  const getPopadStoreCoverage = (storeId: number, formatId: number, defaultCoverage: number) =>
    popadStoreOverrides.get(getPopadStoreOverrideKey(storeId, formatId)) ?? defaultCoverage;

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Reset dependent selections when retailer changes
  useEffect(() => {
    setSelectedCycleId(null);
    setSelectedPacks([]);
    setSelectedStores([]);
    setSelectedMedia([]);
    // Reset POPAd state
    setEnabledPopadFormats(new Set());
    setPopadFormatCoverages(new Map());
    setPopadStoreOverrides(new Map());
  }, [selectedRetailerId]);

  useEffect(() => {
    if (campaignType !== 'manual') {
      setSelectedManualTaskId(null);
    }
  }, [campaignType]);

  // Utility function to check if a pack is flexible (has multiple format group combinations)
  const isFlexiblePack = (pack: any): boolean => {
    if (!pack) return false;

    // Check if pack has formatGroups with multiple groups
    if (pack.formatGroups) {
      try {
        const groups = typeof pack.formatGroups === 'string'
          ? JSON.parse(pack.formatGroups)
          : pack.formatGroups;
        return Array.isArray(groups) && groups.length > 1;
      } catch (error) {
        console.error("Error parsing formatGroups:", error);
        return false;
      }
    }

    // Check if pack has flexibleFormatGroup flag
    return !!pack.flexibleFormatGroup;
  };

  // Get currently selected flexible and non-flexible packs
  const getPackClassification = () => {
    const flexiblePacks = selectedPacks.filter(packId => {
      const pack = packs.find(p => p.id === packId);
      return isFlexiblePack(pack);
    });

    const nonFlexiblePacks = selectedPacks.filter(packId => {
      const pack = packs.find(p => p.id === packId);
      return !isFlexiblePack(pack);
    });

    return { flexiblePacks, nonFlexiblePacks };
  };

  const form = useForm<CampaignFormData>({
    resolver: zodResolver(campaignSchema),
    defaultValues: {
      name: "",
      description: "",
      statusId: 1, // Default to Draft status
      campaignTypeKey: "Standard", // Default campaign type
      createRemoval: false, // Default to not creating removal campaign
    },
  });

  // Use deferred loading - retailers only load when actually needed
  const [shouldLoadRetailers, setShouldLoadRetailers] = useState(false);

  const { data: retailers = [], isLoading: retailersLoading } = useQuery<Retailer[]>({
    queryKey: ["/api/retailers"],
    queryFn: () => getJson("/api/retailers"),
    enabled: shouldLoadRetailers,
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const { data: cycles = [], isLoading: cyclesLoading } = useQuery<BookingCycle[]>({
    queryKey: ["/api/retailers", selectedRetailerId, "cycles"],
    queryFn: async () => {
      try {
        const res = await apiFetch(`/api/retailers/${selectedRetailerId}/cycles`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        return Array.isArray(json) ? json.map(normalizeCycle) : [];
      } catch (error) {
        console.error("Failed to fetch cycles:", error);
        return [];
      }
    },
    enabled: !!selectedRetailerId && currentStep >= 1,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // [PHASE2_PAGINATION] Using unwrapPaginatedResponse for backward compatibility
  const { data: stores = [], isLoading: storesLoading } = useQuery<Store[]>({
    queryKey: ["/api/stores", selectedRetailerId, selectedMedia],
    queryFn: async () => {
      try {
        let url = `/api/stores?retailerId=${selectedRetailerId}&pageSize=200`;
        if (campaignType === 'manual' && selectedMedia.length > 0) {
          const mediaFormatIds = selectedMedia.map(m => m.mediaFormatId).join(',');
          url += `&mediaFormatIds=${mediaFormatIds}`;
        }
        console.log('[CAMPAIGN-CREATION] Fetching stores with URL:', url, 'campaignType:', campaignType, 'selectedMedia:', selectedMedia);
        const res = await apiFetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const data = unwrapPaginatedResponse<Store>(json);
        console.log('[CAMPAIGN-CREATION] Stores received:', data.length, 'stores');
        return data;
      } catch (error) {
        console.error("Failed to fetch stores:", error);
        return [];
      }
    },
    enabled: !!selectedRetailerId && currentStep >= 2,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const { data: packs = [], isLoading: packsLoading } = useQuery<Pack[]>({
    queryKey: ["/api/retailers", selectedRetailerId, "packs"],
    queryFn: async () => {
      try {
        const res = await apiFetch(`/api/retailers/${selectedRetailerId}/packs`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        dbg.log("[packs] fetched", { selectedRetailerId, count: Array.isArray(json) ? json.length : null });
        return json;
      } catch (error) {
        dbg.error("[packs] fetch error", { selectedRetailerId, error });
        throw error;
      }
    },
    enabled: !!selectedRetailerId && currentStep >= 2,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Fetch pack lock statuses when packs are available
  const { data: packStatuses = [], isLoading: packStatusesLoading } = useQuery<Array<{ packId: number, status: 'locked' | 'unlocked' }>>({
    queryKey: ["/api/packs/status-batch", Array.isArray(packs) ? packs.map(p => p.id) : []],
    queryFn: async () => {
      if (!Array.isArray(packs) || !packs.length) return [];
      try {
        const res = await apiFetch('/api/packs/status-batch', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ packIds: packs.map(p => p.id) })
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        dbg.log("[pack-statuses] fetched", { count: Array.isArray(json) ? json.length : null });
        return json;
      } catch (error) {
        dbg.error("[pack-statuses] fetch error", error);
        return [];
      }
    },
    enabled: Array.isArray(packs) && packs.length > 0 && currentStep >= 4,
    staleTime: 30 * 1000, // Shorter cache for lock status
    retry: false,
  });

  const { data: mediaFormats = [], isLoading: mediaLoading } = useQuery<MediaFormat[]>({
    queryKey: ["/api/media-formats", selectedRetailerId],
    queryFn: async () => {
      try {
        const res = await apiFetch(`/api/media-formats?retailerId=${selectedRetailerId}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      } catch (error) {
        console.error("Failed to fetch media formats:", error);
        return [];
      }
    },
    enabled: !!selectedRetailerId && currentStep >= 3,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const { data: groupEditorStores = [], isLoading: groupEditorStoresLoading } = useQuery<Store[]>({
    queryKey: ["/api/retailers", selectedRetailerId, "stores"],
    queryFn: async () => {
      try {
        const res = await apiFetch(`/api/retailers/${selectedRetailerId}/stores`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      } catch (error) {
        console.error("Failed to fetch group editor stores:", error);
        return [];
      }
    },
    enabled: campaignType === 'manual-groups' && !!selectedRetailerId && currentStep >= 4,
    staleTime: 0,
    refetchOnMount: 'always',
    retry: 1,
  });

  // POPAd (Limited-Capacity Format) queries
  const { data: popadFormats = [] } = useQuery<MediaFormat[]>({
    queryKey: ["/api/popad/formats", selectedRetailerId],
    queryFn: async () => {
      try {
        const res = await apiFetch(`/api/popad/formats?retailerId=${selectedRetailerId}`);
        if (!res.ok) return [];
        return res.json();
      } catch (error) {
        console.error("Failed to fetch POPAd formats:", error);
        return [];
      }
    },
    enabled: !!selectedRetailerId && campaignType === 'manual',
    staleTime: 5 * 60 * 1000,
  });

  // POPAd availability for selected cycle
  interface PopadAvailability {
    storeId: number;
    storeName: string;
    mediaFormatId: number;
    mediaFormatName: string;
    capacity: number;
    allocatedUnits: number;
    allocatedPercent: number;
    remainingPercent: number;
    remainingUnits: number;
    isAvailable: boolean;
  }

  // Check if any group has a POPAd format (for manual-groups flow)
  const hasPopadInGroups = useMemo(() => {
    return selectedGroups.some(g => isPopadGroup(g));
  }, [selectedGroups]);

  const { data: popadAvailabilityData } = useQuery<{ cycleId: number; cycleName: string; availability: PopadAvailability[] }>({
    queryKey: ["/api/popad/availability", selectedCycleId],
    queryFn: async () => {
      try {
        const res = await apiFetch(`/api/popad/availability/${selectedCycleId}`);
        if (!res.ok) return { cycleId: selectedCycleId!, cycleName: '', availability: [] };
        return res.json();
      } catch (error) {
        console.error("Failed to fetch POPAd availability:", error);
        return { cycleId: selectedCycleId!, cycleName: '', availability: [] };
      }
    },
    // Enable for both manual flow (popadEnabled) and manual-groups flow (hasPopadInGroups)
    enabled: !!selectedCycleId && (popadEnabled && campaignType === 'manual') || (hasPopadInGroups && campaignType === 'manual-groups'),
    staleTime: 30 * 1000, // Shorter cache for availability
  });

  // Create a map of availability by storeId-formatId for quick lookup
  const popadAvailabilityMap = useMemo(() => {
    const map = new Map<string, PopadAvailability>();
    if (popadAvailabilityData?.availability) {
      for (const avail of popadAvailabilityData.availability) {
        // Key by storeId-formatId to support multiple formats per store
        const key = `${avail.storeId}-${avail.mediaFormatId}`;
        map.set(key, avail);
      }
    }
    return map;
  }, [popadAvailabilityData]);

  // Helper to get availability for a store across all enabled formats
  const getStoreAvailability = (storeId: number): PopadAvailability | null => {
    // Return availability for the first enabled format that has data for this store
    for (const formatId of enabledPopadFormats) {
      const key = `${storeId}-${formatId}`;
      const avail = popadAvailabilityMap.get(key);
      if (avail) return avail;
    }
    return null;
  };

  // Get all availability entries for a store (for all enabled formats)
  const getStoreAvailabilityByFormat = (storeId: number): Map<number, PopadAvailability> => {
    const result = new Map<number, PopadAvailability>();
    for (const formatId of enabledPopadFormats) {
      const key = `${storeId}-${formatId}`;
      const avail = popadAvailabilityMap.get(key);
      if (avail) result.set(formatId, avail);
    }
    return result;
  };

  // Calculate POPAd allocations for selected stores (per format)
  const popadAllocations = useMemo(() => {
    // Check that we have enabled formats with coverage
    if (enabledPopadFormats.size === 0) return [];

    const allocations: Array<{
      storeId: number;
      storeName: string;
      mediaFormatId: number;
      coveragePercent: number;
      plannedUnits: number;
      capacity: number;
    }> = [];

    // For each enabled format with coverage
    for (const formatId of enabledPopadFormats) {
      const coveragePercent = popadFormatCoverages.get(formatId);
      if (!coveragePercent) continue; // Skip if no coverage set

      for (const selection of selectedStores) {
        if (selection.type !== 'store') continue;

        // Use format-specific availability key
        const availKey = `${selection.id}-${formatId}`;
        const avail = popadAvailabilityMap.get(availKey);
        if (!avail || !avail.isAvailable) continue;

        // Use store override if exists, otherwise use format coverage
        const finalCoverage = getPopadStoreCoverage(selection.id, formatId, coveragePercent);
        const plannedUnits = Math.floor(avail.capacity * finalCoverage / 100);

        allocations.push({
          storeId: selection.id,
          storeName: avail.storeName,
          mediaFormatId: formatId,
          coveragePercent: finalCoverage,
          plannedUnits,
          capacity: avail.capacity,
        });
      }
    }

    return allocations;
  }, [enabledPopadFormats, popadFormatCoverages, selectedStores, popadStoreOverrides, popadAvailabilityMap]);

  // POPAd summary stats
  const popadSummary = useMemo(() => {
    const totalStores = new Set(popadAllocations.map(a => a.storeId)).size;
    const totalPlannedUnits = popadAllocations.reduce((sum, a) => sum + a.plannedUnits, 0);
    const coverageBreakdown = {
      25: popadAllocations.filter(a => a.coveragePercent === 25).length,
      50: popadAllocations.filter(a => a.coveragePercent === 50).length,
      75: popadAllocations.filter(a => a.coveragePercent === 75).length,
      100: popadAllocations.filter(a => a.coveragePercent === 100).length,
    };
    return { totalStores, totalPlannedUnits, coverageBreakdown };
  }, [popadAllocations]);

  // Fetch pack snapshots for all packs to avoid conditional hook calls
  const packSnapshots = useMemo(() => {
    return Array.isArray(packs) ? packs.map(pack => pack.id) : [];
  }, [packs]);

  const { data: packSnapshotsData = {} } = useQuery({
    queryKey: ["/api/packs/snapshots-batch", packSnapshots],
    queryFn: async () => {
      if (!packSnapshots.length) return {};

      const results: Record<number, any> = {};

      // Fetch snapshots for all packs in parallel
      const promises = packSnapshots.map(async (packId) => {
        try {
          const res = await apiFetch(`/api/packs/${packId}/snapshot`);
          if (res.ok) {
            const data = await res.json();
            results[packId] = data;
          }
        } catch (error) {
          console.error(`Failed to fetch snapshot for pack ${packId}:`, error);
        }
      });

      await Promise.all(promises);
      return results;
    },
    enabled: !!packSnapshots.length && currentStep >= 4,
    staleTime: 30 * 1000,
    retry: false,
  });

  // TODO: Remove after backend redeploy/backfill updates snapshot shapes in production
  const normalizedStores = useMemo(() => {
    if (!Array.isArray(selectedPacks) || selectedPacks.length === 0) return [] as Array<{ name: string }>;
    const aggregated: Array<{ name: string }> = [];
    const snapshotsObj = (packSnapshotsData && typeof packSnapshotsData === 'object') ? (packSnapshotsData as any) : {};
    selectedPacks.forEach((packId) => {
      const raw = snapshotsObj?.[packId];
      const snapshot = raw && typeof raw === 'object' ? (raw.snapshot ?? {}) : {};
      const storesPrimary = Array.isArray((snapshot as any).stores) ? (snapshot as any).stores : null;
      const storesFallback = !storesPrimary && Array.isArray((snapshot as any).storesList) ? (snapshot as any).storesList : null;
      const source = storesPrimary || storesFallback || [];
      if (Array.isArray(source)) {
        source.forEach((s: any) => {
          aggregated.push({ name: String(s?.name ?? s?.storeName ?? '') });
        });
      }
    });
    return aggregated;
  }, [selectedPacks, packSnapshotsData]);

  // TODO: Remove after backend redeploy/backfill updates snapshot shapes in production
  const normalizedTemplates = useMemo(() => {
    if (!Array.isArray(selectedPacks) || selectedPacks.length === 0) return [] as Array<{ name: string }>;
    const aggregated: Array<{ name: string }> = [];
    const snapshotsObj = (packSnapshotsData && typeof packSnapshotsData === 'object') ? (packSnapshotsData as any) : {};
    selectedPacks.forEach((packId) => {
      const raw = snapshotsObj?.[packId];
      const snapshot = raw && typeof raw === 'object' ? (raw.snapshot ?? {}) : {};
      const templatesPrimary = Array.isArray((snapshot as any).templates) ? (snapshot as any).templates : null;
      const templatesFallback = !templatesPrimary && Array.isArray((snapshot as any).assignedMediaFormats) ? (snapshot as any).assignedMediaFormats : null;
      const source = templatesPrimary || templatesFallback || [];
      if (Array.isArray(source)) {
        source.forEach((t: any) => {
          aggregated.push({ name: String(t?.name ?? t?.mediaFormatName ?? t?.formatName ?? '') });
        });
      }
    });
    return aggregated;
  }, [selectedPacks, packSnapshotsData]);

  const { data: campaignStatuses = [] } = useQuery<CampaignStatus[]>({
    queryKey: ["/api/campaign-statuses"],
    queryFn: () => getJson("/api/campaign-statuses"),
    enabled: currentStep >= 5, // Only load when needed for final step
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const { data: campaignTypes = [] } = useQuery({
    queryKey: ["/api/campaign-types"],
    queryFn: () => getJson("/api/campaign-types"),
    enabled: currentStep >= 5, // Only load when needed for final step
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const { data: advertisers = [] } = useQuery<any[]>({
    queryKey: ["/api/admin/advertisers"],
    queryFn: async () => {
      const response = await apiRequest("/api/admin/advertisers", "GET");
      const json = await response.json();
      return json?.data?.advertisers || [];
    },
    enabled: true, // Always load advertisers
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Fetch operator tasks for manual campaigns
  const { data: operatorTasksData, isLoading: operatorTasksLoading } = useQuery<any[]>({
    queryKey: ["/api/operator-tasks"],
    queryFn: async () => {
      console.log('[CAMPAIGN-CREATION] Fetching operator tasks...');
      try {
        const data = await getJson('/api/operator-tasks');
        console.log('[CAMPAIGN-CREATION] Operator tasks received:', Array.isArray(data) ? data.length : 0, data);
        return Array.isArray(data) ? data : [];
      } catch (error) {
        console.error('[CAMPAIGN-CREATION] Failed to fetch operator tasks:', error);
        return [];
      }
    },
    enabled: (campaignType === 'manual' || campaignType === 'manual-groups') && currentStep >= 4,
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Extract tasks array from response, with safe fallback
  const operatorTasks = operatorTasksData || [];

  // Debug logging for operator tasks
  useEffect(() => {
    if (campaignType === 'manual' || campaignType === 'manual-groups') {
      console.log('[CAMPAIGN-CREATION] Operator tasks state:', {
        loading: operatorTasksLoading,
        count: operatorTasks.length,
        tasks: operatorTasks,
        currentStep,
        enabled: (campaignType === 'manual' || campaignType === 'manual-groups') && currentStep >= 4
      });
    }
  }, [operatorTasks, operatorTasksLoading, campaignType, currentStep]);

  // Prefetch data when retailer is selected to eliminate delays
  useEffect(() => {
    if (selectedRetailerId) {
      queryClient.prefetchQuery({
        queryKey: ["/api/retailers", selectedRetailerId, "cycles"],
        queryFn: () => apiFetch(`/api/retailers/${selectedRetailerId}/cycles`).then(res => res.json()),
        staleTime: 5 * 60 * 1000,
      });
      // [PHASE2_PAGINATION] Using unwrapPaginatedResponse for backward compatibility
      queryClient.prefetchQuery({
        queryKey: ["/api/stores", selectedRetailerId, selectedMedia],
        queryFn: async () => {
          let url = `/api/stores?retailerId=${selectedRetailerId}&pageSize=200`;
          if (campaignType === 'manual' && selectedMedia.length > 0) {
            const mediaFormatIds = selectedMedia.map(m => m.mediaFormatId).join(',');
            url += `&mediaFormatIds=${mediaFormatIds}`;
          }
          const res = await apiFetch(url);
          const json = await res.json();
          return unwrapPaginatedResponse<Store>(json);
        },
        staleTime: 5 * 60 * 1000,
      });
      queryClient.prefetchQuery({
        queryKey: ["/api/retailers", selectedRetailerId, "stores"],
        queryFn: () => apiFetch(`/api/retailers/${selectedRetailerId}/stores`).then(res => res.json()),
        staleTime: 0,
      });
      queryClient.prefetchQuery({
        queryKey: ["/api/retailers", selectedRetailerId, "packs"],
        queryFn: () => apiFetch(`/api/retailers/${selectedRetailerId}/packs`).then(res => res.json()),
        staleTime: 5 * 60 * 1000,
      });
      queryClient.prefetchQuery({
        queryKey: ["/api/media-formats", selectedRetailerId],
        queryFn: () => apiFetch(`/api/media-formats?retailerId=${selectedRetailerId}`).then(res => res.json()),
        staleTime: 5 * 60 * 1000,
      });
    }
  }, [selectedRetailerId, queryClient]);

  // Initialize campaignTypeKey when campaign types are loaded
  useEffect(() => {
    if (Array.isArray(campaignTypes) && campaignTypes.length > 0 && !form.getValues("campaignTypeKey")) {
      const defaultType = campaignTypes.find((type: any) => type.is_default) || campaignTypes[0];
      form.setValue("campaignTypeKey", defaultType.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignTypes]); // Removed 'form' from dependencies to prevent infinite loop

  // Debug logging for retailer/packs state
  useEffect(() => {
    dbg.log("[campaign-creation] retailer/packs state", {
      selectedRetailerId,
      packsCount: Array.isArray(packs) ? packs.length : null,
      sample: Array.isArray(packs) && packs.length > 0 ? packs[0] : null,
    });
  }, [selectedRetailerId, packs]);

  // Ensure UI filters by selected retailer and year even if backend returns superset
  // NOTE: Cycles are reusable across campaigns - do NOT filter by locked_flag
  const displayedCycles = useMemo(() => {
    if (!Array.isArray(cycles)) return [];

    const normalizedTime = (dateValue: string | Date | null | undefined) => {
      if (!dateValue) return -Infinity;
      const ts = new Date(dateValue).getTime();
      return Number.isFinite(ts) ? ts : -Infinity;
    };

    const filtered = cycles.filter((c: any) => {
      if (selectedRetailerId) {
        const cycleRetailerId = Number((c as any)?.retailer_id ?? (c as any)?.retailerId);
        if (cycleRetailerId !== Number(selectedRetailerId)) return false;
      }
      const startDate = c?.start_date ?? c?.startDate;
      const endDate = c?.end_date ?? c?.endDate;
      if (startDate && endDate) {
        const startYear = new Date(startDate).getFullYear();
        const endYear = new Date(endDate).getFullYear();
        return startYear === selectedYear || endYear === selectedYear;
      }
      return true;
    });

    return filtered.sort((a: any, b: any) => {
      const startA = normalizedTime(a?.start_date ?? a?.startDate);
      const startB = normalizedTime(b?.start_date ?? b?.startDate);
      if (startA !== startB) return startA - startB; // earliest start first (12/2025 then 1/2026)

      const endA = normalizedTime(a?.end_date ?? a?.endDate);
      const endB = normalizedTime(b?.end_date ?? b?.endDate);
      if (endA !== endB) return endA - endB;

      return Number(a?.id ?? 0) - Number(b?.id ?? 0);
    });
  }, [cycles, selectedRetailerId, selectedYear]);
  const displayedStores = stores;
  const displayedPacks = Array.isArray(packs) ? packs : [];
  const displayedMediaFormats = mediaFormats || [];

  // For manual campaigns, stores are filtered by retailer and selected media formats
  const filteredStores = useMemo(() => {
    if (!Array.isArray(displayedStores)) return [];

    // Filter by selected retailer first
    if (!selectedRetailerId) return displayedStores;

    let filtered = displayedStores.filter((store: any) => {
      const storeRetailerId = Number(store?.retailerId ?? store?.retailer_id);
      return storeRetailerId === Number(selectedRetailerId);
    });

    // For limited-format-only campaigns (no unlimited formats selected), 
    // only show stores that have availability for at least one enabled format
    const isLimitedOnly = popadEnabled && selectedMedia.length === 0;
    if (isLimitedOnly && popadAvailabilityMap.size > 0 && enabledPopadFormats.size > 0) {
      filtered = filtered.filter((store: any) => {
        // Check if store has availability for any enabled format
        for (const formatId of enabledPopadFormats) {
          const key = `${store.id}-${formatId}`;
          const avail = popadAvailabilityMap.get(key);
          if (avail && avail.isAvailable) return true;
        }
        return false;
      });
    }

    console.log('[CAMPAIGN-CREATION] Filtered stores:', {
      displayedStores: displayedStores.length,
      selectedRetailerId,
      filtered: filtered.length,
      selectedMedia: selectedMedia.length,
      isLimitedOnly,
      enabledFormats: enabledPopadFormats.size,
      popadAvailabilityCount: popadAvailabilityMap.size
    });

    return filtered;
  }, [displayedStores, selectedRetailerId, selectedMedia, popadEnabled, popadAvailabilityMap, enabledPopadFormats]);

  const filteredPacks = useMemo(() => {
    const packsArray = Array.isArray(displayedPacks) ? displayedPacks : [];
    if (campaignType !== 'manual' || selectedMedia.length === 0) return packsArray;

    // Filter packs that have stores with ALL selected media formats available
    return packsArray.filter(pack => {
      // For now, return all packs until we implement proper pack filtering
      return true;
    });
  }, [campaignType, selectedMedia, displayedPacks]);

  // Helper function to wait for snapshot to be ready
  const waitForSnapshot = async (campaignId: number, maxAttempts = 30): Promise<boolean> => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      setSnapshotStatus(`Checking snapshot status... (attempt ${attempt}/${maxAttempts})`);

      try {
        const response = await apiFetch(`/api/campaign-snapshots/${campaignId}/details`);
        if (response.ok) {
          const snapshot = await response.json();
          if (snapshot && snapshot.id && snapshot.campaignId === campaignId) {
            setSnapshotStatus("Snapshot ready!");
            return true;
          }
        }
      } catch (error) {
        console.log(`[SNAPSHOT_WAIT] Attempt ${attempt} failed:`, error);
      }

      // Wait 500ms before next attempt
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    return false;
  };

  const createCampaignMutation = useMutation({
    mutationFn: async (data: ReturnType<typeof buildCampaignPayload>) => {
      return createCampaign(data);
    },
    onSuccess: async (campaign) => {
      // campaign is already parsed JSON from createCampaign()
      const campaignId = campaign.id;
      const isManual = campaignType === 'manual' && selectedStores.length > 0 && selectedMedia.length > 0;

      // Helper to reset form state
      const resetFormState = () => {
        form.reset();
        setCurrentStep(1);
        setSelectedRetailerId(null);
        setSelectedCycleId(null);
        setSelectedStores([]);
        setSelectedPacks([]);
        setSelectedMedia([]);
        setFormData({});
        // Reset POPAd state
        setEnabledPopadFormats(new Set());
        setPopadFormatCoverages(new Map());
        setPopadStoreOverrides(new Map());
      };

      // Create POPAd allocations if enabled (for each enabled format) - OLD MANUAL FLOW
      if (enabledPopadFormats.size > 0 && popadAllocations.length > 0 && selectedCycleId) {
        setSnapshotStatus("Creating POPAd allocations...");
        try {
          // Group allocations by format ID
          const allocationsByFormat = new Map<number, typeof popadAllocations>();
          for (const allocation of popadAllocations) {
            const formatAllocations = allocationsByFormat.get(allocation.mediaFormatId) || [];
            formatAllocations.push(allocation);
            allocationsByFormat.set(allocation.mediaFormatId, formatAllocations);
          }

          // Create allocations for each format
          let allSucceeded = true;
          for (const [mediaFormatId, formatAllocations] of allocationsByFormat) {
            const allocationsPayload = {
              campaignId,
              cycleId: selectedCycleId,
              mediaFormatId,
              storeAllocations: formatAllocations.map(a => ({
                storeId: a.storeId,
                coveragePercent: a.coveragePercent,
              })),
            };

            const response = await apiFetch('/api/popad/allocations', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(allocationsPayload),
            });

            if (!response.ok) {
              console.error(`Failed to create POPAd allocations for format ${mediaFormatId}:`, await response.text());
              allSucceeded = false;
            }
          }

          if (!allSucceeded) {
            toast({
              title: "Warning",
              description: "Campaign created but some POPAd allocations failed. You can add them manually.",
              variant: "default",
            });
          } else {
            // Invalidate POPAd availability cache
            queryClient.invalidateQueries({ queryKey: ['/api/popad/availability'] });
          }
        } catch (error) {
          console.error('Error creating POPAd allocations:', error);
        }
      }

      // Create POPAd allocations for MANUAL-GROUPS flow
      if (campaignType === 'manual-groups' && selectedGroups.length > 0 && selectedCycleId) {
        // Extract POPAd allocations from groups
        const groupPopadAllocations: Array<{ storeId: number; mediaFormatId: number; coveragePercent: number }> = [];
        
        for (const group of selectedGroups) {
          if (isPopadGroup(group)) {
            // Get the POPAd format entry
            const popadEntry = group.formatEntries.find(e => e.coveragePercent && e.coveragePercent > 0);
            if (popadEntry) {
              const defaultCoverage = popadEntry.coveragePercent!;
              
              for (const storeId of group.storeIds) {
                // Use per-store override if available
                const storeCoverage = group.storeCoverageOverrides?.[storeId] ?? defaultCoverage;
                groupPopadAllocations.push({
                  storeId,
                  mediaFormatId: popadEntry.mediaFormatId,
                  coveragePercent: storeCoverage,
                });
              }
            }
          }
        }

        if (groupPopadAllocations.length > 0) {
          setSnapshotStatus("Creating POPAd allocations...");
          try {
            // Group by format ID
            const allocationsByFormat = new Map<number, typeof groupPopadAllocations>();
            for (const allocation of groupPopadAllocations) {
              const formatAllocations = allocationsByFormat.get(allocation.mediaFormatId) || [];
              formatAllocations.push(allocation);
              allocationsByFormat.set(allocation.mediaFormatId, formatAllocations);
            }

            let allSucceeded = true;
            for (const [mediaFormatId, formatAllocations] of allocationsByFormat) {
              const allocationsPayload = {
                campaignId,
                cycleId: selectedCycleId,
                mediaFormatId,
                storeAllocations: formatAllocations.map(a => ({
                  storeId: a.storeId,
                  coveragePercent: a.coveragePercent,
                })),
              };

              const response = await apiFetch('/api/popad/allocations', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(allocationsPayload),
              });

              if (!response.ok) {
                console.error(`Failed to create POPAd allocations for format ${mediaFormatId}:`, await response.text());
                allSucceeded = false;
              }
            }

            if (!allSucceeded) {
              toast({
                title: "Warning",
                description: "Campaign created but some POPAd allocations failed. You can add them manually.",
                variant: "default",
              });
            } else {
              queryClient.invalidateQueries({ queryKey: ['/api/popad/availability'] });
            }

            console.log('[CampaignCreation] Created POPAd allocations for groups:', {
              allocationCount: groupPopadAllocations.length,
              formatCount: allocationsByFormat.size,
            });
          } catch (error) {
            console.error('Error creating POPAd allocations for groups:', error);
          }
        }
      }

      if (isManual) {
        // For manual campaigns, wait for snapshot to be created
        setIsWaitingForSnapshot(true);
        setSnapshotStatus("Creating campaign snapshot...");

        const snapshotReady = await waitForSnapshot(campaignId);

        if (snapshotReady) {
          toast({
            title: "Success",
            description: `Campaign created successfully with snapshot`,
          });
          queryClient.invalidateQueries({ queryKey: ['/api/campaigns'] });
          queryClient.invalidateQueries({ queryKey: ['campaign-snapshot', campaignId] });

          // Reset form and navigate to campaign details
          resetFormState();
          setIsWaitingForSnapshot(false);

          // Navigate to campaign details
          setLocation(`/campaigns/${campaignId}`);
        } else {
          setIsWaitingForSnapshot(false);
          toast({
            title: "Warning",
            description: `Campaign created but snapshot is still being generated. Please refresh the page shortly.`,
            variant: "default",
          });
          queryClient.invalidateQueries({ queryKey: ['/api/campaigns'] });
          resetFormState();
          setLocation(`/campaigns/${campaignId}`);
        }
      } else {
        // Pack-based campaigns: redirect immediately
        toast({
          title: "Success",
          description: `Campaign created successfully`,
        });
        queryClient.invalidateQueries({ queryKey: ['/api/campaigns'] });
        resetFormState();
        setLocation(`/campaigns/${campaignId}`);
      }
    },
    onError: (error: Error) => {
      setIsWaitingForSnapshot(false);
      toast({
        title: "Error",
        description: getCampaignIntegrityUserMessage(error, 'create'),
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: CampaignFormData) => {
    console.log("onSubmit called with data:", data);
    console.log("Form validation errors:", form.formState.errors);

    if (!selectedRetailerId || !selectedCycleId) {
      toast({
        title: "Error",
        description: "Please select a retailer and booking cycle",
        variant: "destructive",
      });
      return;
    }

    // For manual campaigns (old flow), require global operator task
    if (campaignType === 'manual' && (!selectedManualTaskId || !Number.isFinite(selectedManualTaskId))) {
      toast({
        title: "Operator Task Required",
        description: "Select an operator task before creating a manual campaign.",
        variant: "destructive",
      });
      return;
    }

    // For manual-groups campaigns (new flow), all groups require operator task
    if (campaignType === 'manual-groups') {
      const missingOperatorTask = selectedGroups.some(g => 
        !g.operatorTaskId || !Number.isFinite(g.operatorTaskId)
      );
      if (missingOperatorTask) {
        toast({
          title: "Operator Task Required",
          description: "Each group requires an operator task for payment rates. Please check your groups.",
          variant: "destructive",
        });
        return;
      }
    }

    // Build media array including POPAd formats if enabled
    // POPAd formats must be in the media array for the snapshot system to work
    // Each store gets its own entry with the specific planned units for that store
    let mediaForPayload = [...selectedMedia];
    if (enabledPopadFormats.size > 0 && popadAllocations.length > 0) {
      // Add per-store entries for each enabled POPAd format
      for (const allocation of popadAllocations) {
        mediaForPayload.push({
          mediaFormatId: allocation.mediaFormatId,
          quantity: allocation.plannedUnits,
          storeId: allocation.storeId,  // Link to specific store
        });
      }

      console.log('[CampaignCreation] Added POPAd formats per-store:', {
        formatIds: Array.from(enabledPopadFormats),
        allocationCount: popadAllocations.length,
        totalUnits: popadAllocations.reduce((sum, a) => sum + a.plannedUnits, 0)
      });
    }

    // For manual-groups campaigns, flatten groups into stores and media
    let storesForPayload = selectedStores;
    let mediaForPayloadFinal = mediaForPayload;
    let manualTaskIdForPayload = selectedManualTaskId;

    if (campaignType === 'manual-groups' && selectedGroups.length > 0) {
      // Flatten groups to get store×format rows
      const flattenedRows = flattenGroups(selectedGroups);
      
      // Extract unique store IDs
      const uniqueStoreIds = [...new Set(flattenedRows.map(r => r.storeId))];
      storesForPayload = uniqueStoreIds.map(id => ({ type: 'store' as const, id }));
      
      // Convert flattened rows to media format (with storeId for per-store entries)
      // For POPAd entries, calculate actual quantity from coverage × capacity
      mediaForPayloadFinal = flattenedRows.map(row => {
        let quantity = row.quantity;
        
        // If this is a POPAd entry (has coveragePercent), calculate actual units
        if (row.coveragePercent && row.coveragePercent > 0) {
          const availKey = `${row.storeId}-${row.mediaFormatId}`;
          const avail = popadAvailabilityMap.get(availKey);
          if (avail && avail.capacity > 0) {
            quantity = Math.floor((row.coveragePercent / 100) * avail.capacity);
          }
        }
        
        return {
          mediaFormatId: row.mediaFormatId,
          quantity: quantity > 0 ? quantity : 1,
          storeId: row.storeId,
        };
      });
      
      // Set campaign-level task from first group (all groups now have operator tasks)
      // Per-group tasks are stored in groups JSON and used during snapshot creation
      if (selectedGroups.length > 0 && selectedGroups[0].operatorTaskId) {
        manualTaskIdForPayload = selectedGroups[0].operatorTaskId;
      }
      
      console.log('[CampaignCreation] Flattened groups for payload:', {
        groupCount: selectedGroups.length,
        flattenedRows: flattenedRows.length,
        uniqueStores: uniqueStoreIds.length,
        mediaEntries: mediaForPayloadFinal.length,
        manualTaskId: manualTaskIdForPayload,
        totalPopadUnits: mediaForPayloadFinal.filter(m => flattenedRows.find(r => r.storeId === m.storeId && r.mediaFormatId === m.mediaFormatId)?.coveragePercent).reduce((sum, m) => sum + m.quantity, 0),
      });
    }

    const payload = buildCampaignPayload({
      formData: data,
      retailerId: selectedRetailerId,
      cycleId: selectedCycleId,
      packs: selectedPacks,
      stores: storesForPayload,
      media: mediaForPayloadFinal,
      campaignType,
      manualTaskId: manualTaskIdForPayload,
    });

    // Include groups for manual-groups flow (persisted to database for editing/media summary)
    if (campaignType === 'manual-groups' && selectedGroups.length > 0) {
      (payload as any).groups = selectedGroups;
    }

    createCampaignMutation.mutate(payload);
  };

  const nextStep = () => {
    if (currentStep < 6) {
      const newStep = currentStep + 1;
      setCurrentStep(newStep);

      // Trigger retailer loading when moving to retailer selection step
      if (newStep === 2 && !shouldLoadRetailers) {
        setShouldLoadRetailers(true);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return campaignType !== null;
      case 2: return selectedRetailerId !== null;
      case 3: return selectedCycleId !== null;
      case 4:
        // For pack campaigns: need pack selection
        if (campaignType === 'pack') {
          return selectedPacks.length > 0;
        }
        // For manual-groups: need at least one group with stores and formats
        if (campaignType === 'manual-groups') {
          return selectedGroups.length > 0 &&
            selectedGroups.every(g => g.storeIds.length > 0 && g.formatEntries.length > 0);
        }
        // For manual campaigns: need media selection OR POPAd selection with coverage
        const hasUnlimitedFormats = selectedMedia.length > 0;
        // Check that at least one limited format is enabled AND all enabled formats have coverage set
        const hasLimitedFormats = enabledPopadFormats.size > 0 &&
          Array.from(enabledPopadFormats).every(id => popadFormatCoverages.has(id));
        return hasUnlimitedFormats || hasLimitedFormats;
      case 5:
        // For pack campaigns: auto-configured
        if (campaignType === 'pack') {
          return selectedPacks.length > 0;
        }
        // For manual-groups: skip to case 6 (no separate store step)
        if (campaignType === 'manual-groups') {
          return true; // Groups already contain stores
        }
        // For manual: need store selection
        return selectedStores.length > 0 || selectedPacks.length > 0;
      case 6:
        // For manual campaigns (old flow): require global operator task selection
        if (campaignType === 'manual') {
          return form.formState.isValid && !!selectedManualTaskId && (typeof operatorTasksLoading === 'undefined' || !operatorTasksLoading);
        }
        // For manual-groups campaigns (new flow): all groups need operator task
        if (campaignType === 'manual-groups') {
          const allGroupsValid = selectedGroups.every(g => {
            // All groups need operator task
            const hasOperatorTask = g.operatorTaskId && Number.isFinite(g.operatorTaskId);
            if (isPopadGroup(g)) {
              // POPAd groups also need coverage set on format entries
              const hasCoverage = g.formatEntries.every(e => e.coveragePercent && e.coveragePercent > 0);
              return hasOperatorTask && hasCoverage;
            }
            return hasOperatorTask;
          });
          return form.formState.isValid && allGroupsValid;
        }
        // For pack campaigns: no operator task needed
        return form.formState.isValid;
      default: return false;
    }
  };

  const handleStoreToggle = (storeId: number) => {
    const existingIndex = selectedStores.findIndex(s => s.type === 'store' && s.id === storeId);
    if (existingIndex >= 0) {
      setSelectedStores(selectedStores.filter((_, index) => index !== existingIndex));
    } else {
      setSelectedStores([...selectedStores, { type: 'store', id: storeId }]);
    }
  };

  const handlePackToggle = async (packId: number) => {
    const pack = packs.find(p => p.id === packId);
    const isPackFlexible = isFlexiblePack(pack);
    const { flexiblePacks, nonFlexiblePacks } = getPackClassification();

    // Check if pack is already selected
    if (selectedPacks.includes(packId)) {
      // Remove this pack and its related data
      setSelectedPacks(prev => prev.filter(id => id !== packId));
      setSelectedStores(prev => prev.filter(s => s.type !== 'pack' || s.id !== packId));
      setFlexiblePackWarning(""); // Clear warning when removing packs

      // Remove stores and media from this pack using snapshot
      try {
        const snapshotResponse = await apiFetch(`/api/packs/${packId}/snapshot`);
        if (!snapshotResponse.ok) {
          console.warn(`Pack ${packId} snapshot not available, skipping removal`);
          return;
        }
        const snapshotData = await snapshotResponse.json();
        const packStoresData = snapshotData.snapshot?.stores || [];
        const packTemplatesData = snapshotData.snapshot?.templates || [];

        if (packStoresData && Array.isArray(packStoresData)) {
          const packStoreIds = packStoresData.map((ps: any) => ps.storeId);
          setSelectedStores(prev => prev.filter(s =>
            s.type !== 'store' || !packStoreIds.includes(s.id)
          ));
        }

        if (packTemplatesData && Array.isArray(packTemplatesData)) {
          const packMediaIds = packTemplatesData.map((pt: any) => pt.mediaFormatId);
          setSelectedMedia(prev => prev.filter(m =>
            !packMediaIds.includes(m.mediaFormatId)
          ));
        }
      } catch (error) {
        console.error('Error removing pack configuration:', error);
      }

      return;
    }

    // FLEXIBLE PACK VALIDATION: Check restrictions before adding
    if (isPackFlexible) {
      // Rule 1: Only one flexible pack allowed per campaign
      if (flexiblePacks.length > 0) {
        setFlexiblePackWarning("⚠️ You can only select one flexible pack in a campaign.");
        toast({
          title: "Pack Selection Error",
          description: "You can only select one flexible pack in a campaign.",
          variant: "destructive"
        });
        return;
      }

      // Rule 2: Flexible packs cannot be combined with non-flexible packs
      if (nonFlexiblePacks.length > 0) {
        setFlexiblePackWarning("⚠️ Flexible packs cannot be combined with other packs in the same campaign. Please remove other packs to continue.");
        toast({
          title: "Pack Selection Conflict",
          description: "Flexible packs cannot be combined with non-flexible packs. Please remove the existing packs first.",
          variant: "destructive"
        });
        return;
      }

      // Set warning for when flexible pack is selected (all others disabled)
      setFlexiblePackWarning("⚠️ Only one flexible pack can be used per campaign.");
    } else {
      if (flexiblePacks.length > 0) {
        setFlexiblePackWarning("⚠️ Non-flexible packs cannot be combined with flexible packs. Please remove the flexible pack to continue.");
        toast({
          title: "Pack Selection Conflict",
          description: "Non-flexible packs cannot be combined with flexible packs. Please remove the flexible pack first.",
          variant: "destructive"
        });
        return;
      }
      setFlexiblePackWarning(""); // Clear warning if no conflicts
    }

    // Add the new pack and load its configuration
    setSelectedPacks(prev => [...prev, packId]);
    setSelectedStores(prev => [...prev, { type: 'pack', id: packId }]);

    // Fetch and add pack configuration using snapshot
    try {
      const snapshotResponse = await apiFetch(`/api/packs/${packId}/snapshot`);
      if (!snapshotResponse.ok) {
        console.warn(`Pack ${packId} snapshot not available for selection`);
        return;
      }
      const snapshotData = await snapshotResponse.json();
      // TODO: remove once legacy pack snapshots are backfilled on prod
      const snapshot = snapshotData?.snapshot ?? {};

      const packStoresData = Array.isArray(snapshot.stores)
        ? snapshot.stores
        : Array.isArray(snapshot.storesList)
          ? snapshot.storesList
          : [];

      const packTemplatesData = Array.isArray(snapshot.templates)
        ? snapshot.templates
        : Array.isArray(snapshot.assignedMediaFormats)
          ? snapshot.assignedMediaFormats
          : [];



      if (packStoresData && Array.isArray(packStoresData)) {
        // Add pack stores to selections
        const newStoreSelections = packStoresData.map((packStore: any) => ({
          type: 'store' as const,
          id: packStore.storeId
        }));
        setSelectedStores(prev => [...prev, ...newStoreSelections]);

        // Update accumulated pack stores
        setPackStores(prev => [...prev, ...packStoresData]);
      }

      if (packTemplatesData && Array.isArray(packTemplatesData)) {
        // Add pack media selections (merge with existing)
        const newMediaSelections = packTemplatesData.map((template: any) => ({
          mediaFormatId: template.mediaFormatId,
          quantity: template.quantity
        }));

        // Merge with existing media, combining quantities for same format
        setSelectedMedia(prev => {
          const updated = [...prev];
          newMediaSelections.forEach(newMedia => {
            const existingIndex = updated.findIndex(m => m.mediaFormatId === newMedia.mediaFormatId);
            if (existingIndex >= 0) {
              updated[existingIndex].quantity += newMedia.quantity;
            } else {
              updated.push(newMedia);
            }
          });
          return updated;
        });

        // Update accumulated pack templates
        setPackTemplates(prev => [...prev, ...packTemplatesData]);
      }
    } catch (error) {
      console.error('Error fetching pack configuration:', error);
      toast({
        title: "Error",
        description: "Could not load pack configuration",
        variant: "destructive",
      });
    }
  };

  const handleMediaQuantityChange = (mediaFormatId: number, quantity: number) => {
    const existingIndex = selectedMedia.findIndex(m => m.mediaFormatId === mediaFormatId);
    if (quantity === 0) {
      if (existingIndex >= 0) {
        setSelectedMedia(selectedMedia.filter((_, index) => index !== existingIndex));
      }
    } else {
      if (existingIndex >= 0) {
        const updated = [...selectedMedia];
        updated[existingIndex].quantity = quantity;
        setSelectedMedia(updated);
      } else {
        setSelectedMedia([...selectedMedia, { mediaFormatId, quantity }]);
      }
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1: return "Campaign Type";
      case 2: return "Choose Retailer";
      case 3: return "Select Booking Cycle";
      case 4: 
        if (campaignType === 'pack') return "Select Pack";
        if (campaignType === 'manual-groups') return "Create Groups";
        return "Select Media Formats";
      case 5: 
        if (campaignType === 'pack') return "Configure Media";
        if (campaignType === 'manual-groups') return "Execution Preview";
        return "Select Stores";
      case 6: return "Campaign Details";
      default: return "";
    }
  };

  const getCampaignSteps = (): Step[] => {
    // Return different step configurations based on campaign type
    if (campaignType === 'pack') {
      return [
        { title: "Campaign Type", description: "Manual or Pack" },
        { title: "Retailer", description: "Choose retailer" },
        { title: "Booking Cycle", description: "Select cycle" },
        { title: "Select Pack", description: "Choose pack" },
        { title: "Configure Media", description: "Setup media" },
        { title: "Details", description: "Review & submit" },
      ];
    } else if (campaignType === 'manual-groups') {
      return [
        { title: "Campaign Type", description: "Manual or Pack" },
        { title: "Retailer", description: "Choose retailer" },
        { title: "Booking Cycle", description: "Select cycle" },
        { title: "Create Groups", description: "Formats & stores" },
        { title: "Execution Preview", description: "Verify assignments" },
        { title: "Details", description: "Submit" },
      ];
    } else {
      // Default to manual campaign steps (old flow)
      return [
        { title: "Campaign Type", description: "Manual or Pack" },
        { title: "Retailer", description: "Choose retailer" },
        { title: "Booking Cycle", description: "Select cycle" },
        { title: "Media Formats", description: "Choose formats" },
        { title: "Select Stores", description: "Pick stores" },
        { title: "Details", description: "Review & submit" },
      ];
    }
  };

  const handleStepClick = (stepIndex: number) => {
    // Allow navigation to any completed step (steps before current)
    // stepIndex is 0-based, currentStep is 1-based
    const targetStep = stepIndex + 1;
    if (targetStep < currentStep) {
      setCurrentStep(targetStep);
    }
  };

  // Show immediate loading skeleton while retailers load
  if (retailersLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Create Campaign</h1>
            <p className="text-muted-foreground mt-2">
              Setting up your new media campaign...
            </p>
          </div>
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span>Choose Retailer</span>
              <Badge variant="outline">1/5</Badge>
            </CardTitle>
            <Progress value={20} className="w-full" />
          </CardHeader>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Choose Retailer</CardTitle>
            <p className="text-sm text-muted-foreground">
              Select the retailer for this campaign
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-center items-center h-32">
              <div className="text-muted-foreground">Loading retailers...</div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Helper function to get pack lock status
  const getPackStatus = (packId: number): 'locked' | 'unlocked' | 'loading' => {
    if (packStatusesLoading) return 'loading';
    const status = packStatuses.find(s => s.packId === packId);
    return status?.status || 'unlocked';
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Create Campaign</h1>
          <p className="text-muted-foreground mt-2">
            Set up a new media campaign following the step-by-step process
          </p>
        </div>
      </div>

      {/* Stepper Progress Indicator */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between mb-4">
            <span>{getStepTitle()}</span>
            <Badge variant="outline">{currentStep}/6</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pb-6">
          <Stepper
            steps={getCampaignSteps()}
            currentStep={currentStep - 1}
            onStepClick={handleStepClick}
          />
        </CardContent>
      </Card>

      {/* Step 1: Campaign Type Selection */}
      {currentStep === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Campaign Type
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Choose how you want to create this campaign
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card
                className={cn(
                  "cursor-pointer transition-colors p-6",
                  campaignType === 'manual' ? "ring-2 ring-primary" : "hover:bg-muted"
                )}
                onClick={() => setCampaignType('manual')}
              >
                <div className="flex items-center space-x-3">
                  <div className="flex-1">
                    <h3 className="font-semibold flex items-center gap-2">
                      <MousePointer className="h-4 w-4" />
                      Manual Campaign
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Build one group of formats and stores manually.
                    </p>
                  </div>
                </div>
              </Card>

              <Card
                className={cn(
                  "cursor-pointer transition-colors p-6",
                  campaignType === 'pack' ? "ring-2 ring-primary" : "hover:bg-muted"
                )}
                onClick={() => setCampaignType('pack')}
              >
                <div className="flex items-center space-x-3">
                  <div className="flex-1">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      Pack-Based Campaign
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Build a campaign using the retailer's pre-configured store and format packs.
                    </p>
                  </div>
                </div>
              </Card>

              {/* Pack + Manual Campaign - Admin only */}
              {isAdmin && (
                <Card
                  className={cn(
                    "cursor-pointer transition-colors p-6 relative",
                    campaignType === 'manual-groups' ? "ring-2 ring-primary" : "hover:bg-muted"
                  )}
                  onClick={() => setCampaignType('manual-groups')}
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex-1">
                      <h3 className="font-semibold flex items-center gap-2">
                        <StoreIcon className="h-4 w-4" />
                        Pack + Manual Campaign
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Build campaigns with multiple groups — start from scratch or populate from packs
                      </p>
                    </div>
                  </div>
                </Card>
              )}

              {/* Import Campaign (format matrix) - Admin only, Beta */}
              {isAdmin && (
                <Card
                  className="cursor-pointer transition-colors p-6 relative hover:bg-muted"
                  onClick={() => setLocation('/campaign-upload')}
                >
                  <Badge className="absolute top-2 right-2 text-xs bg-amber-500 text-white hover:bg-amber-600">
                    Beta
                  </Badge>
                  <div className="flex items-center space-x-3">
                    <div className="flex-1">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Upload className="h-4 w-4" />
                        Import Campaign (By Format)
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Master Install List import with format mapping, operator coverage, and kitting reconciliation.
                      </p>
                    </div>
                  </div>
                </Card>
              )}

              {/* Import Campaign by Pack - Admin only, Beta */}
              {isAdmin && (
                <Card
                  className="cursor-pointer transition-colors p-6 relative hover:bg-muted"
                  onClick={() => setLocation('/campaign-upload-packs')}
                >
                  <Badge className="absolute top-2 right-2 text-xs bg-amber-500 text-white hover:bg-amber-600">
                    Beta
                  </Badge>
                  <div className="flex items-center space-x-3">
                    <div className="flex-1">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        Import Campaign (By Pack)
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Bulk create draft pack campaigns from a campaign booking sheet (campaign name + pack name).
                      </p>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Retailer Selection */}
      {currentStep === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>Choose Retailer</CardTitle>
            <p className="text-sm text-muted-foreground">
              Select the retailer for this campaign
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {retailersLoading ? (
              <InlineLoadingSpinner text="Loading retailers..." />
            ) : (
              <div className="space-y-2">
                <Label htmlFor="retailer-select">Retailer</Label>
                <Combobox
                  options={retailers.map(r => ({
                    value: r.id.toString(),
                    label: r.name,
                  }))}
                  value={selectedRetailerId?.toString() || ""}
                  onValueChange={(value) => setSelectedRetailerId(value ? parseInt(value) : null)}
                  placeholder="Search retailers..."
                  searchPlaceholder="Search retailers..."
                  emptyText="No retailers found."
                />
                {selectedRetailerId && (
                  <div className="mt-4 p-4 bg-muted rounded-lg">
                    <div className="flex items-center gap-3">
                      {retailers.find(r => r.id === selectedRetailerId)?.logo ? (
                        <img
                          src={retailers.find(r => r.id === selectedRetailerId)?.logo ?? undefined}
                          alt="Retailer logo"
                          className="h-12 w-12 object-contain rounded-md bg-white p-1"
                        />
                      ) : (
                        <Building2 className="h-12 w-12 text-muted-foreground" />
                      )}
                      <div>
                        <h3 className="font-medium">{retailers.find(r => r.id === selectedRetailerId)?.name}</h3>
                        {retailers.find(r => r.id === selectedRetailerId)?.description && (
                          <p className="text-sm text-muted-foreground">
                            {retailers.find(r => r.id === selectedRetailerId)?.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 3: Cycle Selection */}
      {currentStep === 3 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Select Booking Cycle
              {campaignType && (
                <Badge variant={campaignType === 'pack' ? 'secondary' : 'default'} className="ml-2">
                  {campaignType === 'manual' ? 'Manual Campaign' :
                    campaignType === 'manual-groups' ? 'Pack + Manual Campaign' :
                      'Pack-Based Campaign'}
                </Badge>
              )}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Choose the booking cycle for this campaign
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Year Filter */}
            <div className="flex items-center justify-between pb-2 border-b">
              <YearFilter showIcon={true} label="Filter by Year" />
            </div>
            {cyclesLoading ? (
              <div className="space-y-4">
                <InlineLoadingSpinner text="Loading booking cycles..." />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3].map((i) => (
                    <Card key={i} className="animate-pulse">
                      <CardContent className="p-4">
                        <div className="space-y-2">
                          <div className="h-4 bg-muted rounded w-3/4"></div>
                          <div className="h-3 bg-muted rounded w-1/2"></div>
                          <div className="h-3 bg-muted rounded w-2/3"></div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ) : displayedCycles.length === 0 ? (
              <div className="text-center p-8">
                <div className="text-muted-foreground mb-2">
                  No booking cycles available for this retailer
                </div>
                <p className="text-sm text-red-600 dark:text-red-400">
                  Please contact an administrator to set up booking cycles for this retailer before creating campaigns.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {displayedCycles.map((cycle) => (
                  <Card
                    key={cycle.id}
                    className={cn(
                      "cursor-pointer transition-colors",
                      selectedCycleId === cycle.id ? "ring-2 ring-primary" : "hover:bg-muted"
                    )}
                    onClick={() => setSelectedCycleId(cycle.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Calendar className="h-8 w-8 text-muted-foreground" />
                        <div className="flex-1">
                          <h3 className="font-medium">{cycle.name}</h3>
                          <p className="text-sm text-muted-foreground">
                            {new Date(cycle.startDate).toLocaleDateString()} - {new Date(cycle.endDate).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 4: Pack Selection (pack-based campaigns) */}
      {currentStep === 4 && campaignType === 'pack' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Select Campaign Pack
              <Badge variant="secondary" className="ml-2">
                Pack-Based Campaign
              </Badge>
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Choose a pre-configured pack that includes stores and media templates
            </p>
          </CardHeader>
          <CardContent>
            {packsLoading ? (
              <div className="flex justify-center items-center h-32">
                <div className="text-muted-foreground">Loading packs...</div>
              </div>
            ) : displayedPacks.length === 0 ? (
              <div className="text-center p-8">
                <div className="text-muted-foreground mb-2">
                  No store packs available for this retailer
                </div>
                <p className="text-sm text-red-600 dark:text-red-400">
                  Please contact an administrator to create store packs for this retailer before using pack-based campaigns.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">
                    {selectedPacks.length > 0 ? `${selectedPacks.length} pack${selectedPacks.length === 1 ? '' : 's'} selected` : 'No packs selected'}
                  </span>
                  <Badge variant="outline">
                    {displayedPacks.length} available
                  </Badge>
                </div>

                {/* Flexible Pack Warning */}
                {flexiblePackWarning && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800 dark:text-amber-200">{flexiblePackWarning}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {displayedPacks.map((pack) => {
                    const isSelected = selectedPacks.includes(pack.id);
                    const isPackFlexible = isFlexiblePack(pack);
                    const { flexiblePacks, nonFlexiblePacks } = getPackClassification();

                    // Enhanced disable logic for single flexible pack rule
                    let isDisabled = false;
                    let disableReason = "";

                    if (!isSelected) {
                      if (isPackFlexible) {
                        // Disable if another flexible pack is already selected
                        if (flexiblePacks.length > 0) {
                          isDisabled = true;
                          disableReason = "Only one flexible pack allowed per campaign";
                        }
                        // Or if non-flexible packs are selected
                        else if (nonFlexiblePacks.length > 0) {
                          isDisabled = true;
                          disableReason = "Cannot combine flexible pack with non-flexible packs";
                        }
                      } else {
                        // Disable non-flexible if any flexible pack is selected
                        if (flexiblePacks.length > 0) {
                          isDisabled = true;
                          disableReason = "Cannot combine with flexible pack already selected";
                        }
                      }
                    }

                    return (
                      <PackCard
                        key={pack.id}
                        pack={pack}
                        isSelected={isSelected}
                        onToggle={() => handlePackToggle(pack.id)}
                        isDisabled={isDisabled}
                        disableReason={disableReason}
                        lockStatus={getPackStatus(pack.id)}
                        snapshotData={packSnapshotsData[pack.id]}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}





      {/* Step 4: Media Format Selection (manual campaigns) */}
      {currentStep === 4 && campaignType === 'manual' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Select Media Formats
              <Badge variant="default" className="ml-2">
                Manual Campaign
              </Badge>
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Choose which media formats to include in your campaign
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {mediaLoading ? (
              <div className="flex justify-center items-center h-32">
                <div className="text-muted-foreground">Loading media formats...</div>
              </div>
            ) : (
              <>
                {/* Unlimited Capacity Formats Section */}
                {displayedMediaFormats.length > 0 && (
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h3 className="font-semibold text-blue-800 flex items-center gap-2 mb-2">
                      <Package className="h-5 w-5" />
                      Unlimited Capacity Formats
                    </h3>
                    <p className="text-sm text-blue-700 mb-4">
                      Standard formats available at all selected stores.
                    </p>

                    <div className="space-y-3">
                      {displayedMediaFormats.map((format) => (
                        <div key={format.id} className="flex items-center justify-between bg-white p-3 rounded border border-blue-100">
                          <div className="flex-1">
                            <h4 className="font-medium">{format.name}</h4>
                            {format.description && (
                              <p className="text-sm text-muted-foreground">{format.description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Label htmlFor={`quantity-${format.id}`} className="text-sm text-muted-foreground">Quantity Per Store (Store Selection on Next Step):</Label>
                            <Input
                              id={`quantity-${format.id}`}
                              type="number"
                              min="0"
                              className="w-20"
                              value={selectedMedia.find(m => m.mediaFormatId === format.id)?.quantity || 0}
                              onChange={(e) => handleMediaQuantityChange(format.id, parseInt(e.target.value) || 0)}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {displayedMediaFormats.length === 0 && popadFormats.length === 0 && (
                  <div className="text-center text-muted-foreground p-8">
                    No media formats available
                  </div>
                )}
              </>
            )}

            {/* Limited-Capacity Formats Section */}
            {popadFormats.length > 0 && (
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <h3 className="font-semibold text-amber-800 flex items-center gap-2 mb-2">
                  <ShoppingCart className="h-5 w-5" />
                  Limited Capacity Formats
                </h3>
                <p className="text-sm text-amber-700 mb-4">
                  These formats depend on store capacity and remaining availability per cycle.
                </p>

                <div className="space-y-3">
                  {popadFormats.map((format) => {
                    const isFormatEnabled = enabledPopadFormats.has(format.id);
                    const formatCoverage = popadFormatCoverages.get(format.id);

                    return (
                      <div key={format.id} className="flex items-center justify-between bg-white p-3 rounded border border-amber-100">
                        <div className="flex items-center gap-3">
                          <Checkbox
                            id={`popad-${format.id}`}
                            checked={isFormatEnabled}
                            onCheckedChange={(checked) => {
                              const newEnabled = new Set(enabledPopadFormats);
                              if (checked) {
                                newEnabled.add(format.id);
                              } else {
                                newEnabled.delete(format.id);
                                // Also remove the coverage for this format
                                const newCoverages = new Map(popadFormatCoverages);
                                newCoverages.delete(format.id);
                                setPopadFormatCoverages(newCoverages);
                              }
                              setEnabledPopadFormats(newEnabled);
                            }}
                          />
                          <div>
                            <Label htmlFor={`popad-${format.id}`} className="font-medium cursor-pointer">
                              {format.name}
                            </Label>
                            {format.description && (
                              <p className="text-xs text-muted-foreground">{format.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Use resolver to get dynamic bands - supports legacy and new formats */}
                          {(() => {
                            const bookingConfig = resolveManualCampaignBooking({
                              capacityType: (format as any).capacityType,
                              bookingType: (format as any).bookingType,
                              bookingMeta: (format as any).bookingMeta,
                            });

                            if (bookingConfig.bookingType === "percentage") {
                              const bands = bookingConfig.bands;
                              return (
                                <>
                                  <Label className="text-sm text-muted-foreground">Coverage:</Label>
                                  <Select
                                    disabled={!isFormatEnabled}
                                    value={formatCoverage?.toString() || ""}
                                    onValueChange={(value) => {
                                      const newCoverages = new Map(popadFormatCoverages);
                                      newCoverages.set(format.id, parseInt(value));
                                      setPopadFormatCoverages(newCoverages);
                                    }}
                                  >
                                    <SelectTrigger className="w-28">
                                      <SelectValue placeholder="Select %" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {bands.map(band => (
                                        <SelectItem key={band} value={band.toString()}>
                                          {band}%
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </>
                              );
                            } else if (bookingConfig.bookingType === "absolute") {
                              // For absolute booking, show a numeric input instead
                              return (
                                <>
                                  <Label className="text-sm text-muted-foreground">Units:</Label>
                                  <Input
                                    type="number"
                                    min="1"
                                    max={bookingConfig.absoluteLimitPerStore || 10}
                                    disabled={!isFormatEnabled}
                                    value={formatCoverage || ""}
                                    onChange={(e) => {
                                      const newCoverages = new Map(popadFormatCoverages);
                                      newCoverages.set(format.id, parseInt(e.target.value) || 1);
                                      setPopadFormatCoverages(newCoverages);
                                    }}
                                    className="w-20"
                                  />
                                  <span className="text-xs text-muted-foreground">
                                    (max: {bookingConfig.absoluteLimitPerStore || 10})
                                  </span>
                                </>
                              );
                            }

                            // Fallback for unknown booking types
                            return null;
                          })()}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {enabledPopadFormats.size > 0 && Array.from(enabledPopadFormats).some(id => !popadFormatCoverages.has(id)) && (
                  <p className="text-sm text-amber-600 mt-3 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    Please select a coverage % for each selected format to continue
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 4: Store + Format Groups (manual-groups campaigns) */}
      {currentStep === 4 && campaignType === 'manual-groups' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <StoreIcon className="h-5 w-5" />
              Store + Format Groups
              <Badge variant="default" className="ml-2">
                Pack + Manual Campaign
              </Badge>
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Create groups of stores and formats. Start from scratch or populate from packs.
            </p>
          </CardHeader>
          <CardContent>
            <StoreFormatGroupEditor
              campaignId={null} // Draft state, no campaign ID yet
              retailerId={selectedRetailerId ?? 0}
              cycleId={selectedCycleId}
              groups={selectedGroups}
              onGroupsChange={setSelectedGroups}
              stores={groupEditorStores}
              storesLoading={groupEditorStoresLoading}
              availablePacks={displayedPacks}
              packSnapshotsData={packSnapshotsData}
              readOnly={false}
            />
          </CardContent>
        </Card>
      )}

      {/* Step 5: Execution Preview (manual-groups campaigns) */}
      {currentStep === 5 && campaignType === 'manual-groups' && (
        <ExecutionPreview
          groups={selectedGroups}
          onBack={prevStep}
          onContinue={nextStep}
          isSubmitting={createCampaignMutation.isPending || isWaitingForSnapshot}
        />
      )}

      {/* Step 5: Store/Pack Selection (only for manual campaigns) */}
      {currentStep === 5 && campaignType === 'manual' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <StoreIcon className="h-5 w-5" />
              Select Stores
              <Badge variant="default" className="ml-2">
                Manual Campaign
              </Badge>
              {(selectedMedia.length > 0 || popadEnabled) && (
                <Badge variant="outline" className="ml-2">
                  {filteredStores.length} stores available
                </Badge>
              )}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Choose individual stores for your campaign
              {selectedMedia.length > 0 && (
                <span className="block mt-1 text-primary">
                  {selectedMedia.length} unlimited format{selectedMedia.length === 1 ? '' : 's'} selected
                </span>
              )}
              {enabledPopadFormats.size > 0 && popadFormatCoverages.size > 0 && (
                <span className="block mt-1 text-amber-600">
                  {enabledPopadFormats.size} limited capacity format{enabledPopadFormats.size === 1 ? '' : 's'} enabled
                </span>
              )}
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {selectedMedia.length === 0 && !popadEnabled ? (
              <div className="text-center text-muted-foreground p-8">
                Please select media formats in the previous step before choosing stores
              </div>
            ) : (
              <div className="space-y-6">
                {/* Individual Store Selection */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium">Individual Stores</h3>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const allStoreIds = filteredStores.map(store => store.id);
                          const allSelected = allStoreIds.every(id =>
                            selectedStores.some(s => s.type === 'store' && s.id === id)
                          );

                          if (allSelected) {
                            // Deselect all stores
                            setSelectedStores(selectedStores.filter(s => s.type !== 'store'));
                          } else {
                            // Select all stores
                            const newStoreSelections = allStoreIds.map(id => ({ type: 'store' as const, id }));
                            const nonStoreSelections = selectedStores.filter(s => s.type !== 'store');
                            setSelectedStores([...nonStoreSelections, ...newStoreSelections]);
                          }
                        }}
                        className="text-xs"
                      >
                        {(() => {
                          const allSelected = filteredStores.length > 0 &&
                            filteredStores.every(store =>
                              selectedStores.some(s => s.type === 'store' && s.id === store.id)
                            );
                          return allSelected ? 'Deselect All' : 'Select All';
                        })()}
                      </Button>
                      <Badge variant="secondary">
                        {filteredStores.length} available
                      </Badge>
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground mb-4">
                    {popadEnabled && selectedMedia.length === 0
                      ? "Stores with available capacity for selected limited capacity formats"
                      : "Stores that have all selected media formats available"
                    }
                  </div>
                  {storesLoading ? (
                    <div className="flex justify-center items-center h-32">
                      <div className="text-muted-foreground">Loading stores...</div>
                    </div>
                  ) : filteredStores.length === 0 ? (
                    <div className="text-center text-muted-foreground p-8">
                      {popadEnabled && selectedMedia.length === 0
                        ? "No stores with available capacity for this cycle"
                        : "No stores available with the selected media formats"
                      }
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredStores.map((store) => {
                          // Get availability for all enabled formats for this store
                          const storeFormatAvailability = getStoreAvailabilityByFormat(store.id);
                          const hasLimitedFormats = popadEnabled && storeFormatAvailability.size > 0;

                          // Check eligibility: store is eligible if ALL enabled formats have sufficient availability
                          let isEligible = hasLimitedFormats;
                          let ineligibleReason = '';
                          if (hasLimitedFormats) {
                            for (const formatId of enabledPopadFormats) {
                              const avail = storeFormatAvailability.get(formatId);
                              const requiredCoverage = popadFormatCoverages.get(formatId) || 0;
                              if (!avail) {
                                isEligible = false;
                                const formatName = popadFormats.find(f => f.id === formatId)?.name || 'Unknown';
                                ineligibleReason = `No capacity for ${formatName}`;
                                break;
                              }
                              if (!avail.isAvailable || avail.remainingPercent < requiredCoverage) {
                                isEligible = false;
                                ineligibleReason = `${avail.mediaFormatName}: ${avail.remainingPercent}% available, ${requiredCoverage}% requested`;
                                break;
                              }
                            }
                          }

                          const isStoreSelected = selectedStores.some(s => s.type === 'store' && s.id === store.id);

                          return (
                            <Card key={store.id} className={cn(
                              "cursor-pointer transition-colors",
                              isStoreSelected ? "ring-2 ring-primary" : "hover:bg-muted",
                              hasLimitedFormats && !isEligible && "opacity-60"
                            )}>
                              <CardContent className="p-4">
                                <div className="flex items-start space-x-3">
                                  <Checkbox
                                    checked={isStoreSelected}
                                    onCheckedChange={() => handleStoreToggle(store.id)}
                                    className="mt-1"
                                    disabled={popadEnabled && hasLimitedFormats && !isEligible}
                                  />
                                  <div className="flex-1">
                                    <h3 className="font-medium">{store.name}</h3>
                                    <p className="text-sm text-muted-foreground">
                                      {store.address}
                                    </p>

                                    {/* Unlimited Formats Information */}
                                    {selectedMedia.length > 0 && (
                                      <div className="mt-2 p-2 rounded text-xs bg-blue-50 border border-blue-200">
                                        <div className="flex items-center justify-between mb-1">
                                          <span className="font-medium text-blue-800">Formats</span>
                                          <Badge
                                            variant="default"
                                            className="text-xs bg-blue-500"
                                          >
                                            {selectedMedia.reduce((sum, m) => sum + m.quantity, 0)} units
                                          </Badge>
                                        </div>
                                        <div className="space-y-0.5 text-blue-700">
                                          {selectedMedia.map((media) => {
                                            const format = displayedMediaFormats.find(f => f.id === media.mediaFormatId);
                                            return format ? (
                                              <div key={media.mediaFormatId} className="flex justify-between">
                                                <span>{format.name}</span>
                                                <span className="font-medium">×{media.quantity}</span>
                                              </div>
                                            ) : null;
                                          })}
                                        </div>
                                      </div>
                                    )}

                                    {/* Limited Capacity Format Information - show each format separately */}
                                    {hasLimitedFormats && (
                                      <div className={cn(
                                        "mt-2 p-2 rounded text-xs",
                                        isEligible ? "bg-amber-50 border border-amber-200" : "bg-gray-100 border border-gray-200"
                                      )}>
                                        <div className="space-y-2">
                                          {Array.from(enabledPopadFormats).map(formatId => {
                                            const avail = storeFormatAvailability.get(formatId);
                                            const format = popadFormats.find(f => f.id === formatId);
                                            const requiredCoverage = popadFormatCoverages.get(formatId) || 0;
                                            const formatEligible = avail && avail.isAvailable && avail.remainingPercent >= requiredCoverage;
                                            const storeCoverage = getPopadStoreCoverage(store.id, formatId, requiredCoverage);
                                            const plannedUnits = avail ? Math.floor((storeCoverage / 100) * avail.capacity) : 0;

                                            return (
                                              <div key={formatId} className="space-y-2">
                                                <div className="flex items-center justify-between">
                                                  <span className={cn(
                                                    "font-medium",
                                                    formatEligible ? "text-amber-800" : "text-gray-500"
                                                  )}>
                                                    {format?.name || 'Unknown Format'}
                                                  </span>
                                                  {avail ? (
                                                    <Badge
                                                      variant={formatEligible ? "default" : "secondary"}
                                                      className={cn("text-xs", formatEligible ? "bg-amber-500" : "")}
                                                    >
                                                      {avail.remainingPercent}% avail ({avail.capacity} units)
                                                    </Badge>
                                                  ) : (
                                                    <Badge variant="secondary" className="text-xs">
                                                      No capacity
                                                    </Badge>
                                                  )}
                                                </div>

                                                {isStoreSelected && avail && (
                                                  <div className="flex items-center gap-1 flex-wrap">
                                                    {[25, 50, 75, 100].map(pct => {
                                                      const canSelect = avail.remainingPercent >= pct;
                                                      const isActive = storeCoverage === pct;

                                                      return (
                                                        <button
                                                          key={pct}
                                                          type="button"
                                                          disabled={!canSelect}
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            setPopadStoreOverrides(prev => {
                                                              const next = new Map(prev);
                                                              const overrideKey = getPopadStoreOverrideKey(store.id, formatId);

                                                              if (pct === requiredCoverage) {
                                                                next.delete(overrideKey);
                                                              } else {
                                                                next.set(overrideKey, pct);
                                                              }

                                                              return next;
                                                            });
                                                          }}
                                                          className={cn(
                                                            "px-2 py-0.5 text-xs rounded border transition-colors",
                                                            isActive
                                                              ? "bg-amber-600 text-white border-amber-600"
                                                              : canSelect
                                                                ? "bg-white text-amber-700 border-amber-300 hover:bg-amber-100"
                                                                : "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                                                          )}
                                                        >
                                                          {pct}%
                                                        </button>
                                                      );
                                                    })}
                                                    <span className="text-xs text-amber-800 ml-1">
                                                      = {plannedUnits} units
                                                    </span>
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>

                                        {!isEligible && ineligibleReason && (
                                          <p className="text-red-600 mt-2 text-xs">{ineligibleReason}</p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>

                      <div className="mt-4 p-3 bg-muted rounded-lg">
                        <div className="text-sm font-medium">
                          Selected: {selectedStores.filter(s => s.type === 'store').length} of {filteredStores.length} stores
                        </div>
                        {selectedStores.filter(s => s.type === 'store').length > 0 && (
                          <div className="text-xs text-muted-foreground mt-1">
                            Selected stores will be included in the campaign
                          </div>
                        )}
                      </div>

                      {/* Unlimited Formats Summary Panel */}
                      {selectedMedia.length > 0 && selectedStores.filter(s => s.type === 'store').length > 0 && (
                        <Card className="mt-4 bg-blue-50 border-blue-200">
                          <CardHeader className="py-3">
                            <CardTitle className="text-sm flex items-center gap-2 text-blue-800">
                              <Package className="h-4 w-4" />
                              Unlimited Formats Summary
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="py-2 space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span>Total Stores Selected:</span>
                              <span className="font-medium">{selectedStores.filter(s => s.type === 'store').length}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Units per Store:</span>
                              <span className="font-medium">{selectedMedia.reduce((sum, m) => sum + m.quantity, 0)}</span>
                            </div>
                            <Separator className="my-2" />
                            <div className="flex justify-between font-semibold text-blue-900">
                              <span>Total Units Planned:</span>
                              <span>{(selectedStores.filter(s => s.type === 'store').length * selectedMedia.reduce((sum, m) => sum + m.quantity, 0)).toLocaleString()}</span>
                            </div>
                            <div className="text-xs text-muted-foreground mt-2">
                              Format breakdown:
                              {selectedMedia.map((media, idx) => {
                                const format = displayedMediaFormats.find(f => f.id === media.mediaFormatId);
                                const storeCount = selectedStores.filter(s => s.type === 'store').length;
                                return format ? (
                                  <span key={media.mediaFormatId}>
                                    {idx > 0 ? ' • ' : ' '}
                                    {format.name}: {(media.quantity * storeCount).toLocaleString()} units
                                  </span>
                                ) : null;
                              })}
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Limited Capacity Formats Summary Panel */}
                      {enabledPopadFormats.size > 0 && popadFormatCoverages.size > 0 && (
                        <Card className="mt-4 bg-amber-50 border-amber-200">
                          <CardHeader className="py-3">
                            <CardTitle className="text-sm flex items-center gap-2 text-amber-800">
                              <ShoppingCart className="h-4 w-4" />
                              Limited Capacity Formats Summary
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="py-2 space-y-2 text-sm">
                            {/* Show each enabled format */}
                            {Array.from(enabledPopadFormats).map(formatId => {
                              const format = popadFormats.find(f => f.id === formatId);
                              const coverage = popadFormatCoverages.get(formatId);
                              return format && coverage ? (
                                <div key={formatId} className="flex justify-between">
                                  <span>{format.name}:</span>
                                  <span className="font-medium">{coverage}% coverage</span>
                                </div>
                              ) : null;
                            })}
                            <Separator className="my-2" />
                            <div className="flex justify-between">
                              <span>Total Stores:</span>
                              <span className="font-medium">{popadSummary.totalStores}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Total Units Planned:</span>
                              <span className="font-medium">{popadSummary.totalPlannedUnits.toLocaleString()}</span>
                            </div>
                            {popadSummary.totalStores === 0 && (
                              <p className="text-amber-600 flex items-center gap-1">
                                <AlertCircle className="h-4 w-4" />
                                No stores selected with availability
                              </p>
                            )}
                          </CardContent>
                        </Card>
                      )}
                    </>
                  )}
                </div>


              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 5: Pack Summary (for pack-based campaigns) */}
      {currentStep === 5 && campaignType === 'pack' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Pack Configuration Summary
              <Badge variant="secondary" className="ml-2">
                Pack-Based Campaign
              </Badge>
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Review the stores and media formats included in your selected pack
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {selectedPacks.length > 0 ? (
              <div className="space-y-6">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-green-800">
                    <Package className="h-5 w-5" />
                    <span className="font-medium">
                      {selectedPacks.length} pack{selectedPacks.length === 1 ? '' : 's'} automatically configured
                    </span>
                  </div>
                  <p className="text-sm text-green-700 mt-1">
                    Stores and media formats have been automatically included from the selected pack template{selectedPacks.length === 1 ? '' : 's'}.
                  </p>
                </div>

                {/* Show selected packs */}
                <div className="space-y-2">
                  <h4 className="font-medium">Selected Packs</h4>
                  <div className="space-y-1">
                    {selectedPacks.map((packId) => {
                      const pack = displayedPacks.find(p => p.id === packId);
                      return pack ? (
                        <div key={packId} className="text-sm text-muted-foreground">
                          • {pack.name}
                        </div>
                      ) : (
                        <div key={packId} className="text-sm text-muted-foreground">
                          • Pack ID {packId}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium mb-2">Included Stores</h4>
                    <div className="space-y-1">
                      {Array.isArray(normalizedStores) && normalizedStores.length > 0 ? (
                        normalizedStores.map((s: any, index: number) => (
                          <div key={`norm-store-${index}`} className="text-sm text-muted-foreground">• {s.name || 'Unknown'}</div>
                        ))
                      ) : (
                        packStores.length > 0 ? (
                          packStores.map((packStore: any, index: number) => {
                            const store = stores?.find((s: any) => s.id === packStore.storeId);
                            const pack = displayedPacks.find(p => p.id === packStore.packId);
                            return store ? (
                              <div key={`${packStore.storeId}-${packStore.packId || index}`} className="text-sm text-muted-foreground">
                                • {store.name} {pack ? `(from ${pack.name})` : ''}
                              </div>
                            ) : (
                              <div key={`store-${packStore.storeId}-${packStore.packId || index}`} className="text-sm text-muted-foreground">
                                • Store ID {packStore.storeId} {pack ? `(from ${pack.name})` : ''}
                              </div>
                            );
                          })
                        ) : (
                          <div className="text-sm text-muted-foreground">No stores configured</div>
                        )
                      )}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">Media Configuration</h4>
                    <div className="space-y-3">
                      {(() => {
                        // Check if any selected pack is flexible
                        const flexiblePack = selectedPacks.length > 0 ?
                          displayedPacks.find(pack => {
                            if (!selectedPacks.includes(pack.id)) return false;

                            // Check acceptableFormatGroups field only
                            let formatGroups;
                            try {
                              if (pack.acceptableFormatGroups) {
                                formatGroups = typeof pack.acceptableFormatGroups === 'string'
                                  ? JSON.parse(pack.acceptableFormatGroups)
                                  : pack.acceptableFormatGroups;
                              }
                              return Array.isArray(formatGroups) && formatGroups.length > 1;
                            } catch {
                              return false;
                            }
                          }) : null;

                        if (flexiblePack) {
                          // Render flexible pack format groups
                          let formatGroups;
                          try {
                            formatGroups = flexiblePack.acceptableFormatGroups
                              ? (typeof flexiblePack.acceptableFormatGroups === 'string'
                                ? JSON.parse(flexiblePack.acceptableFormatGroups)
                                : flexiblePack.acceptableFormatGroups)
                              : [];
                          } catch {
                            formatGroups = [];
                          }

                          const groupLabels = ['Primary Option', 'Alternative 1', 'Alternative 2', 'Alternative 3', 'Alternative 4'];

                          return (
                            <div className="space-y-3">
                              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-sm font-medium text-blue-800">
                                  The operator will choose the most appropriate format group on the day of installation.
                                </p>
                              </div>

                              <div className="space-y-4">
                                {formatGroups.map((group: any[], groupIndex: number) => (
                                  <div key={groupIndex} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                                    <h5 className="font-medium text-sm mb-2 text-gray-800">
                                      Group {String.fromCharCode(65 + groupIndex)} – {groupLabels[groupIndex] || `Option ${groupIndex + 1}`}
                                    </h5>
                                    <div className="space-y-1">
                                      {group.map((formatSpec: any, formatIndex: number) => {
                                        const format = mediaFormats?.find((f: any) => f.id === formatSpec.formatId);
                                        return (
                                          <div key={formatIndex} className="text-sm text-gray-600 ml-3">
                                            • {format ? format.name : `Media Format ${formatSpec.formatId}`}: {formatSpec.quantity}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        } else {
                          // Render regular media format list for standard packs
                          if (Array.isArray(normalizedTemplates) && normalizedTemplates.length > 0) {
                            return (
                              <div className="space-y-1">
                                {normalizedTemplates.map((t: any, index: number) => (
                                  <div key={`norm-template-${index}`} className="text-sm text-muted-foreground">• {t.name || 'Unknown'}</div>
                                ))}
                              </div>
                            );
                          }
                          return packTemplates.length > 0 ? (
                            <div className="space-y-1">
                              {packTemplates.map((template: any, index: number) => {
                                const format = mediaFormats?.find((f: any) => f.id === template.mediaFormatId);
                                const pack = displayedPacks.find(p => p.id === template.packId);
                                return format ? (
                                  <div key={`${template.mediaFormatId}-${template.packId || index}`} className="text-sm text-muted-foreground">
                                    • {format.name}: {template.quantity} units {pack ? `(from ${pack.name})` : ''}
                                  </div>
                                ) : (
                                  <div key={`format-${template.mediaFormatId}-${template.packId || index}`} className="text-sm text-muted-foreground">
                                    • Media Format {template.mediaFormatId}: {template.quantity} units {pack ? `(from ${pack.name})` : ''}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-sm text-muted-foreground">No media formats configured</div>
                          );
                        }
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-muted-foreground p-8">
                No pack selected
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 6: Campaign Details */}
      {currentStep === 6 && (
        <Card>
          <CardHeader>
            <CardTitle>Campaign Details</CardTitle>
            <p className="text-sm text-muted-foreground">
              Enter the campaign details and create your campaign
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Campaign Name</Label>
                <Input
                  id="name"
                  {...form.register("name")}
                  placeholder="Enter campaign name"
                />
                {form.formState.errors.name && (
                  <p className="text-sm text-destructive">{form.formState.errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description (Optional)</Label>
                <Textarea
                  id="description"
                  {...form.register("description")}
                  placeholder="Enter campaign description"
                  rows={3}
                />
              </div>

              {/* Operator Task Selection - MANUAL CAMPAIGNS ONLY (old flow) */}
              {/* For manual-groups (new flow), operator task is selected per-group in the group editor */}
              {campaignType === 'manual' && (
                <div className="space-y-2">
                  <Label htmlFor="manual-task-select" className="text-sm font-medium">
                    Operator Task <span className="text-destructive">*</span>
                  </Label>
                  {(typeof operatorTasksLoading !== 'undefined' && operatorTasksLoading) ? (
                    <InlineLoadingSpinner text="Loading operator tasks..." />
                  ) : (
                    <Select
                      value={selectedManualTaskId ? String(selectedManualTaskId) : ""}
                      onValueChange={(value) => {
                        if (!value) {
                          setSelectedManualTaskId(null);
                          return;
                        }
                        if (value === "__none") {
                          setSelectedManualTaskId(null);
                          return;
                        }
                        const parsed = Number(value);
                        if (Number.isFinite(parsed) && parsed > 0) {
                          setSelectedManualTaskId(parsed);
                        } else {
                          setSelectedManualTaskId(null);
                        }
                      }}
                      disabled={operatorTasks.length === 0}
                    >
                      <SelectTrigger id="manual-task-select">
                        <SelectValue placeholder={operatorTasks.length === 0 ? 'No operator tasks available' : 'Select an operator task'} />
                      </SelectTrigger>
                      <SelectContent>
                        {operatorTasks.length === 0 ? (
                          <SelectItem value="__none" disabled>
                            No operator tasks available
                          </SelectItem>
                        ) : (
                          operatorTasks.map((task) => (
                            <SelectItem key={task.id} value={String(task.task_id)}>
                              {task.task_name || task.name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  )}
                  {selectedManualTaskId === null && (typeof operatorTasksLoading === 'undefined' || !operatorTasksLoading) && operatorTasks.length > 0 && (
                    <p className="text-sm text-muted-foreground">Select an operator task to continue.</p>
                  )}
                  {!selectedManualTaskId && (
                    <p className="text-sm text-muted-foreground">
                      Required: This task will be assigned to operators for installation.
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="campaignTypeKey">Campaign Type</Label>
                <Select
                  value={form.watch("campaignTypeKey") || "Standard"}
                  onValueChange={(value) => form.setValue("campaignTypeKey", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select campaign type" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.isArray(campaignTypes) && campaignTypes
                      .filter((type: any) => type.name.toLowerCase() !== 'removal')
                      .map((type: any) => (
                        <SelectItem key={`campaign-type-${type.id}`} value={type.name}>
                          {type.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="statusId">Status</Label>
                <Select
                  defaultValue="1"
                  onValueChange={(value) => form.setValue("statusId", parseInt(value))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(campaignStatuses as CampaignStatus[]).map((status) => (
                      <SelectItem key={status.id} value={status.id.toString()}>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: status.color || '#9CA3AF' }}
                          />
                          {status.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="advertiserId">Advertiser (Optional)</Label>
                <Combobox
                  options={Array.isArray(advertisers) && advertisers.length > 0 ? advertisers.map((advertiser: any) => ({
                    value: advertiser.id.toString(),
                    label: advertiser.name,
                  })) : []}
                  value={String(form.watch("advertiserId") ?? "")}
                  onValueChange={(value) => form.setValue("advertiserId", value ? parseInt(value, 10) : null)}
                  placeholder="Search advertisers..."
                  searchPlaceholder="Search advertisers..."
                  emptyText="No advertisers found."
                />
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="font-medium">Campaign Summary</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Retailer:</span>
                    <span className="ml-2 font-medium">
                      {retailers.find(r => r.id === selectedRetailerId)?.name}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Booking Cycle:</span>
                    <span className="ml-2 font-medium">
                      {displayedCycles.find(c => c.id === selectedCycleId)?.name}
                    </span>
                  </div>
                  {campaignType === 'manual-groups' ? (
                    <>
                      <div>
                        <span className="text-muted-foreground">Groups:</span>
                        <span className="ml-2 font-medium">
                          {selectedGroups.length}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Total Stores:</span>
                        <span className="ml-2 font-medium">
                          {new Set(selectedGroups.flatMap(g => g.storeIds)).size}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Total Formats:</span>
                        <span className="ml-2 font-medium">
                          {new Set(selectedGroups.flatMap(g => g.formatEntries.map(e => e.mediaFormatId))).size}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Total Assignments:</span>
                        <span className="ml-2 font-medium">
                          {selectedGroups.reduce((sum, g) => sum + (g.storeIds.length * g.formatEntries.length), 0)}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <span className="text-muted-foreground">Individual Stores:</span>
                        <span className="ml-2 font-medium">
                          {selectedStores.filter(s => s.type === 'store').length}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Store Packs:</span>
                        <span className="ml-2 font-medium">
                          {selectedPacks.length}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Media Formats:</span>
                        <span className="ml-2 font-medium">
                          {selectedMedia.length}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Total Media Units:</span>
                        <span className="ml-2 font-medium">
                          {selectedMedia.reduce((sum, m) => sum + m.quantity, 0)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* POPAd Summary Panel (Step 6 Review) */}
              {enabledPopadFormats.size > 0 && popadAllocations.length > 0 && (
                <div className="space-y-4">
                  <Separator />
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <h3 className="font-medium flex items-center gap-2 text-amber-800 mb-3">
                      <ShoppingCart className="h-5 w-5" />
                      Limited Capacity Formats
                    </h3>

                    <div className="space-y-2 text-sm">
                      {/* Show each enabled format with its coverage */}
                      {Array.from(enabledPopadFormats).map(formatId => {
                        const format = popadFormats.find(f => f.id === formatId);
                        const coverage = popadFormatCoverages.get(formatId);
                        return format && coverage ? (
                          <div key={formatId} className="flex justify-between">
                            <span className="text-muted-foreground">{format.name}:</span>
                            <span className="font-medium">{coverage}% coverage</span>
                          </div>
                        ) : null;
                      })}
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total Stores:</span>
                        <span className="font-medium">{popadSummary.totalStores}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total Units Planned:</span>
                        <span className="font-medium">{popadSummary.totalPlannedUnits.toLocaleString()}</span>
                      </div>
                    </div>

                    {/* Show per-store allocations if there are overrides */}
                    {popadStoreOverrides.size > 0 && (
                      <div className="mt-3 pt-3 border-t border-amber-200">
                        <p className="text-xs text-muted-foreground mb-2">Store Overrides:</p>
                        <div className="space-y-1 text-xs max-h-32 overflow-y-auto">
                          {popadAllocations
                            .filter(a => popadStoreOverrides.has(getPopadStoreOverrideKey(a.storeId, a.mediaFormatId)))
                            .map(a => (
                              <div key={`${a.storeId}-${a.mediaFormatId}`} className="flex justify-between text-amber-700">
                                <span>{a.storeName} • {popadFormats.find(f => f.id === a.mediaFormatId)?.name || `Format ${a.mediaFormatId}`}</span>
                                <span>{a.coveragePercent}% → {a.plannedUnits} units</span>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    <div className="mt-3 p-2 bg-amber-100 rounded text-xs text-amber-800">
                      <Info className="h-3 w-3 inline mr-1" />
                      These allocations will be reserved for this campaign in the selected cycle.
                    </div>
                  </div>
                </div>
              )}
            </form>
          </CardContent>
        </Card>
      )}

      {/* Loading Modal for Snapshot Creation */}
      <Dialog open={isWaitingForSnapshot}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Creating Campaign Snapshot</DialogTitle>
            <DialogDescription>
              Please wait while we create the campaign snapshot. This may take a few moments...
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center py-6 space-y-4">
            <LoadingSpinner />
            <p className="text-sm text-muted-foreground text-center">
              {snapshotStatus || "Processing..."}
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Navigation - hidden for step 5 with manual-groups since ExecutionPreview has its own navigation */}
      {!(currentStep === 5 && campaignType === 'manual-groups') && (
        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            onClick={() => {
              console.log("Previous button clicked!");
              prevStep();
            }}
            disabled={currentStep === 1}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>

          {/* Phase 4: Soft Nudge for Manual Campaign Creation */}
          {currentStep === 6 && (
            <p className="text-xs text-muted-foreground italic flex items-center gap-1.5 mx-4">
              <Info className="h-3 w-3" />
              This campaign will not be linked to a booking plan
            </p>
          )}

          {/* Debug button to test event handling */}
          {currentStep < 6 ? (
            <Button
              onClick={nextStep}
              disabled={!canProceed()}
            >
              Next
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={(e) => {
                console.log("Create Campaign button clicked");
                console.log("Can proceed:", canProceed());
                console.log("Is pending:", createCampaignMutation.isPending);
                try {
                  console.log("Form valid:", form.formState.isValid);
                  console.log("Form errors:", form.formState.errors);
                  console.log("Selected data:", {
                    retailerId: selectedRetailerId,
                    cycleId: selectedCycleId,
                    stores: selectedStores,
                    packs: selectedPacks,
                    media: selectedMedia
                  });

                  // Call form submission manually
                  const formData = form.getValues();
                  console.log("Form data:", formData);
                  onSubmit(formData);
                } catch (error) {
                  console.error("Error in button click:", error);
                }
              }}
              disabled={!canProceed() || createCampaignMutation.isPending}
            >
              {createCampaignMutation.isPending ? (
                "Creating Campaign..."
              ) : (
                "Create Campaign"
              )}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
